import * as THREE from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import helvetikerBold from "three/examples/fonts/helvetiker_bold.typeface.json";
import { GameScene, themeFor } from "./Scene";
import { CAMERA_TUNING } from "./theme";
import { Player } from "./Player";
import { World } from "./World";
import { Path } from "./Path";
import { SANDBOX_CONFIG } from "./sandboxConfig";
import { buildHitParadeCannons, buildHitParadeStartPlatform, type MovingPlatform } from "./hitParadeDecor";
import { buildIceCrackCourse, buildSnowfall, type IceCrackHandle } from "./iceCrackDecor";
import { buildDoorDashCourse, type DoorDashHandle } from "./doorDashDecor";
import { buildRollingTubesCourse, type RollingTubesHandle } from "./rollingTubesDecor";
import { buildCircleSpinnerCourse, type CircleSpinnerHandle } from "./circleSpinnerDecor";
import { buildDoubleSpinnerArena, type DoubleSpinnerHandle } from "./doubleSpinnerArena";
import { buildMazeRushCourse, type MazeRushHandle } from "./mazeRushDecor";
import { buildSpaceShipCourse, type SpaceShipHandle } from "./spaceShipDecor";
import { buildSupermarketSweepCourse, type SupermarketSweepHandle } from "./supermarketSweepDecor";
import { buildBlockClimbCourse, type BlockClimbHandle } from "./blockClimbDecor";
import { buildRiverBoatCourse, type RiverBoatHandle } from "./riverBoatDecor";
import { buildTrainRoofCourse, type TrainRoofHandle } from "./trainRoofDecor";
import { buildCoinMesh, buildGateMesh, buildSpawnPlan } from "./Spawner";
import { isLowQuality } from "./quality";
import { isFallingLog, type ObstacleContact, type ObstacleController } from "./obstacles";
import { Ghost, computeSlotOffset } from "./Ghost";
import { Input } from "./Input";
import { LANE_POSITIONS, RUNNER } from "@shared/constants";
import { upgradeEffect } from "@shared/upgrades";
import { sfx, playBattleRoyaleAnnounce } from "../audio";
import type {
  GameConfig,
  GameEventHandler,
  RunOutcome,
  RunState,
  SpawnedCoin,
  WordGate,
} from "../types";
import type { GameSettings } from "../../local/gameSettings";

export class Game {
  private scene: GameScene;
  /** Container that holds every level mesh + actor. Lifted to
   *  `SANDBOX_CONFIG.floatingLevelBaseY` so the whole obstacle course
   *  rides high in the sky and no system below it can act as an
   *  invisible floor. World↔local Y is reconciled in the foot-raycast
   *  by subtracting `courseRoot.position.y` from hit points before
   *  computing the player's path-relative landing height. */
  private courseRoot: THREE.Group;
  private world: World;
  private player: Player;
  private input: Input;
  private path: Path;

  private obstacles: ObstacleController[] = [];
  private coins: SpawnedCoin[] = [];
  private gates: WordGate[] = [];

  private coinMeshes = new Map<number, THREE.Object3D>();
  private gateMeshes = new Map<number, THREE.Object3D>();

  private wordOutcomes: { wordId: string; correct: boolean }[] = [];
  private runState: RunState;
  private rafId = 0;
  private lastFrame = 0;
  /** Minimum ms between rendered frames. Low tier caps to 30fps for
   *  CONSISTENT pacing on weak GPUs (better than a stuttering 20–45). 0 =
   *  uncapped (render every rAF). */
  private frameMinMs = isLowQuality() ? 1000 / 30 : 0;
  private disposed = false;
  private suspendedSpeed: number | null = null;

  // Camera shake.
  private shakeUntil = 0;
  private shakeStart = 0;
  private shakeAmp = 0;
  // Slow-mo + death sequence.
  private timeScale = 1;
  private lastDt = 1 / 60;
  private hitParadeTick: ((now: number) => void) | null = null;
  private iceCrack: IceCrackHandle | null = null;
  private snowfallTick: ((dt: number, x: number, y: number, z: number) => void) | null = null;
  private doorDash: DoorDashHandle | null = null;
  private rollingTubes: RollingTubesHandle | null = null;
  private circleSpinner: CircleSpinnerHandle | null = null;
  private doubleSpinner: DoubleSpinnerHandle | null = null;
  private mazeRush: MazeRushHandle | null = null;
  private spaceShip: SpaceShipHandle | null = null;
  private supermarketSweep: SupermarketSweepHandle | null = null;
  private blockClimb: BlockClimbHandle | null = null;
  private riverBoat: RiverBoatHandle | null = null;
  private trainRoof: TrainRoofHandle | null = null;
  /** Committed maze-rush lane index (0..2) while inside a section, else -1. */
  private mazeRushLaneIdx: number = -1;
  /** While falling between lanes, the player's world-Y offset above the
   *  path. Updated by gravity each frame. Null when grounded on a lane. */
  private mazeRushFallY: number | null = null;
  /** Vertical velocity during between-lane fall (m/s). */
  private mazeRushFallVY: number = 0;
  /** Date.now() at game construction — used as the clockOrigin fallback when
   *  the config doesn't provide one (solo mode). */
  private startedAtMs = Date.now();
  /** After-finish spectator state. Local player is hidden and the camera
   *  follows whichever ghost has this id. Null until enterSpectator() fires. */
  private spectateTargetId: string | null = null;
  /** All moving platforms in the level. Designed to scale to multi-platform
   *  levels: just push more platforms onto this list. */
  private platforms: MovingPlatform[] = [];
  /** Per-platform last-known state, indexed by array position. */
  private platformStates: Array<{ s: number; centerL?: number; topY: number; halfDepth: number; halfWidth: number; halfHeight: number; velocityS: number } | null> = [];
  /** Index of the platform the player is currently riding, or -1. */
  private ridingPlatform = -1;
  /** Captured (s, l) offsets while on a platform — drives the rigid carry. */
  private platformOffsetS = 0;
  private platformOffsetL = 0;
  /** Platform velocity carried into the air for a short window after jump. */
  private platformLaunchVel = 0;
  private platformLaunchUntil = 0;
  private dying = false;
  private deathStartedAt = 0;
  private pendingDeath: RunOutcome | null = null;
  /** Performance.now() the player started sinking in level-8 water,
   *  or null when not currently sinking. Used to play a short splash-
   *  sink before triggering respawn. */
  private waterSinkAt: number | null = null;
  /** Smoothed world-Y the chase camera targets. Lerps toward the
   *  player's true world Y to absorb the instantaneous jumps that
   *  happen when raycast support snaps the player onto a higher
   *  step (level 3 pyramid). */
  private camSmoothedY = NaN;
  /** Performance.now() the shark caught the player, or null when not
   *  in a shark-death sequence. Drives the slow-mo crumble + delayed
   *  respawn. */
  private sharkDeathAt: number | null = null;

  /** True when the player is currently OVER the river (i.e. in a gap
   *  between docks, off any boat, with no support below). On the
   *  river-boat level this means they're "in the water" — moving
   *  slow + being hunted by the shark. */
  private isPlayerInRiverWater(): boolean {
    const rb = this.path.riverBoat;
    if (!rb) return false;
    const s = this.runState.distance;
    // On a dock? Then they're walking on wood, not water.
    if (rb.docks.some((d) => s >= d.start && s <= d.end)) return false;
    // Above a boat? Not in water either.
    if (this.ridingPlatform >= 0) return false;
    // Otherwise they're hovering over a gap → splashing.
    return true;
  }
  /** Performance.now() when the player most recently lost standable
   *  support AND started actually dropping (vy < 0). Cleared the moment
   *  they touch a surface again or jump back up. Used to fire a clean
   *  2-second respawn if they're truly in free fall — no animation, no
   *  camera lock, no SFX. */
  private unsupportedSince: number | null = null;
  /** Exposed for GameView so the network broadcast can flip to state="falling"
   *  while the local player is mid-fall — true during a real freefall (no
   *  support + descending, before the 2s respawn/elimination fires) so peers
   *  see the fall animated, e.g. an eliminated survival player dropping away. */
  isFalling(): boolean {
    return this.unsupportedSince != null && this.runState.finishedAt == null;
  }
  private fallRespawnAtS = 0;
  /** Arc-length the player was placed at on spawn. Used to detect
   *  "still standing at spawn" → keep snapping; once they've moved
   *  past it the per-frame snap stops so question-gate pauses don't
   *  visually re-drop the runner. */
  private spawnAtS = 0;
  /** Highest s the player has reached since the last forward-progress reset
   *  on a successful respawn. */
  private highWaterS = 0;
  private supportRaycaster = new THREE.Raycaster();
  private supportMeshes: THREE.Object3D[] = [];
  private hasStandableSupport = false;

  // Upgrade-driven values.
  private coinMagnetRadius = 0.7;
  private coinPickupValue = 1;
  private quizCorrectCoins = 5;
  private quizBonusCoins = 0;
  private winnerChestCoins = 0;
  private extraInvulnerableMs = 0;
  /** Path-s of the most recent question gate the player has crossed. -1
   *  before they reach any gate (respawn falls back to the start). */
  private lastCheckpointS = -1;
  private knockSlowUntil = 0;
  private playerKnockbackSpeed = 0;
  private jumpCarrySpeed = 0;
  private wasAirborne = false;
  /** performance.now() of the last jump that actually fired. River-boat
   *  (shark) level throttles jumps to once per second so players can't
   *  spam-hop across and skip the wading sections. */
  private lastJumpAt = -Infinity;
  /** Last assist-mode auto-jump trigger time, used to throttle. */
  private lastAutoJumpAt = 0;
  /** Live override for assist mode set by the in-game pause toggle. Null = use
   *  config.assistMode (the value baked in at construction). */
  private assistOverride: boolean | null = null;
  /** Toggle assist on/off while the game is running. */
  setAssistMode(on: boolean) { this.assistOverride = on; }
  /** Read current effective assist setting. */
  getAssistMode(): boolean { return this.assistOverride ?? this.config.assistMode ?? false; }

  // Multiplayer ghost opponents (id → Ghost).
  private ghosts = new Map<string, Ghost>();
  private ghostStates = new Map<string, string>();
  private ghostContactUntil = new Map<string, number>();
  // In-world start countdown (3, 2, 1, GO) as 3D extruded text that flies
  // out of the distance and rushes through the camera each beat.
  private countdownGroup: THREE.Group | null = null;
  private countdownTextMesh: THREE.Mesh | null = null;
  private countdownTextMat: THREE.MeshStandardMaterial | null = null;
  private static countdownFont: Font | null = null;
  private countdownRings: THREE.Object3D[] = [];
  private countdownLabel: string | null = null;
  private countdownChangedAt = 0;
  private flyoverStartedAt = 0;
  // Aligned with LessonPlay's START_FLYOVER_MS so the countdown only kicks
  // off once the camera has finished its sweep to the start line.
  private flyoverDurationMs = 4800;
  private moveAxis = 0;
  private forwardAxis = 0;
  private lookAxisX = 0;
  private lookAxisY = 0;
  private cameraYawOffset = 0;
  private cameraPitchOffset = 0;
  private localMoveForward = 0;
  /** After a question-gate resume, require the player to release forward
   *  input (drop below threshold) before we honor it again. Prevents a
   *  held key / joystick from auto-walking the player on resume. */
  private forwardInputGate = false;
  /** performance.now() at which forwardInputGate was last raised. We
   *  auto-clear after a hard timeout so a flaky input source (no fresh
   *  setMoveVector deliveries, virtual joystick released without ever
   *  re-firing) can't trap the player forever. */
  private forwardInputGateSince = 0;
  private localMoveLateral = 0;

  /** MP: feed in the latest network position for an opponent. */
  setRemotePosition(
    id: string,
    s: number,
    laneOffset: number,
    state = "run",
    velocityS = 0,
    velocityL = 0,
    jumpSeq?: number,
    y?: number,
    vy?: number,
    grounded?: boolean,
  ) {
    const g = this.ghosts.get(id);
    if (g) {
      g.setTarget(s, laneOffset, velocityS, velocityL, y, vy, grounded);
      g.setState(state as "run" | "jump" | "hit" | "finished");
      g.pulseJump(jumpSeq);
      this.ghostStates.set(id, state);
    }
  }

  /** MP: keep the ghost roster in sync without rebuilding the whole Game.
   *  Adds any new players, removes any departed ones. This lets RacePlay
   *  push roster updates imperatively so a player join/leave broadcast
   *  doesn't reset everyone's race progress. */
  syncRemotePlayers(roster: Array<{
    id: string;
    displayName: string;
    avatarConfig: import("@shared/types").AvatarConfig;
    slotIdx?: number;
  }>) {
    const incoming = new Set<string>();
    for (const rp of roster) {
      incoming.add(rp.id);
      if (this.ghosts.has(rp.id)) continue;
      const g = new Ghost(rp.id, rp.displayName, rp.avatarConfig, rp.slotIdx ?? 0, this.path);
      g.s = this.runState.distance; // park them near the local player
      g.targetS = g.s;
      this.ghosts.set(rp.id, g);
      this.courseRoot.add(g.group);
    }
    // Remove ghosts whose ids are no longer in the roster. Detach from the
    // correct parent (courseRoot — where they were added) and dispose their
    // geometry/materials/textures so long 8-player sessions don't leak.
    for (const id of Array.from(this.ghosts.keys())) {
      if (!incoming.has(id)) {
        const g = this.ghosts.get(id);
        if (g) {
          this.courseRoot.remove(g.group);
          g.dispose();
        }
        this.ghosts.delete(id);
        this.ghostStates.delete(id);
      }
    }
  }

  /** Current player s-distance — exposed so RacePlay can persist it as a
   *  resume safety net in case the Game gets rebuilt mid-race. */
  getCurrentDistance(): number {
    return this.runState.distance;
  }

  /** Current respawn checkpoint s. */
  getCurrentCheckpointS(): number {
    return this.fallRespawnAtS;
  }

  /** Show/update the in-world arcade countdown. Pass null to hide it. */
  setCountdownLabel(label: string | null) {
    if (this.countdownLabel === label) return;
    this.countdownLabel = label;
    this.countdownChangedAt = performance.now();
    if (!label) {
      if (this.countdownGroup) this.countdownGroup.visible = false;
      return;
    }
    if (this.runState.distance < 0.05) {
      this.runState.startedAt = performance.now();
      this.emitState();
    }
    sfx.countdown(label);
    this.ensureCountdownGroup();
    if (!this.countdownGroup || !this.countdownTextMesh) return;
    this.countdownGroup.visible = true;
    this.countdownTextMesh.geometry.dispose();
    this.countdownTextMesh.geometry = this.buildCountdownGeometry(label);
    if (this.countdownTextMat) this.countdownTextMat.opacity = 1;
  }

  constructor(
    private canvas: HTMLCanvasElement,
    private config: GameConfig,
    private onEvent: GameEventHandler,
  ) {
    const theme = themeFor(config.theme, config.timeOfDay);
    this.scene = new GameScene(canvas, theme);
    this.courseRoot = new THREE.Group();
    this.courseRoot.position.y = SANDBOX_CONFIG.floatingLevelBaseY;
    this.scene.scene.add(this.courseRoot);
    this.path = new Path(config.courseLength, config.seed, config.courseMode ?? "default");
    this.world = new World(theme, config.courseLength, this.path, {
      noWalls:
        config.courseMode === "hit-parade" ||
        config.courseMode === "ice-crack" ||
        config.courseMode === "door-dash" ||
        config.courseMode === "rolling-tubes" ||
        config.courseMode === "circle-spinner" ||
        config.courseMode === "double-spinner" ||
        config.courseMode === "maze-rush" ||
        config.courseMode === "space-ship" ||
        config.courseMode === "supermarket-sweep" ||
        config.courseMode === "block-climb" ||
        config.courseMode === "river-boat" ||
        config.courseMode === "train-roof",
      hideGround:
        config.courseMode === "ice-crack" ||
        config.courseMode === "door-dash" ||
        config.courseMode === "rolling-tubes" ||
        config.courseMode === "circle-spinner" ||
        config.courseMode === "double-spinner" ||
        config.courseMode === "maze-rush" ||
        config.courseMode === "space-ship" ||
        config.courseMode === "supermarket-sweep" ||
        config.courseMode === "block-climb" ||
        config.courseMode === "river-boat" ||
        config.courseMode === "train-roof",
      // Survival arena has no finish line — the crowd lives around the
      // arena itself, built inside doubleSpinnerArena.
      hideFinish: config.courseMode === "double-spinner",
      // Low quality kills the ambient atmosphere layer (birds, particles).
      ambientDecor: this.scene.quality.enableAmbientDecor,
      // Survival arena floats alone in clear sky — drop the along-path cloud
      // clusters that would otherwise sit directly under the platform.
      skyClouds: config.courseMode !== "double-spinner",
    });
    this.courseRoot.add(this.world.group);
    if (config.courseMode === "ice-crack") {
      const ice = buildIceCrackCourse(this.path);
      this.courseRoot.add(ice.group);
      this.iceCrack = ice;
      // Snowfall particles are the heaviest decor — skip on low quality.
      if (this.scene.quality.enableAmbientDecor) {
        const snow = buildSnowfall();
        this.courseRoot.add(snow.group);
        this.snowfallTick = snow.tick;
      }
    }
    if (config.courseMode === "door-dash") {
      const dd = buildDoorDashCourse(this.path, config.seed);
      this.courseRoot.add(dd.group);
      this.doorDash = dd;
    }
    if (config.courseMode === "rolling-tubes") {
      const rt = buildRollingTubesCourse(this.path);
      this.courseRoot.add(rt.group);
      this.rollingTubes = rt;
    }
    if (config.courseMode === "circle-spinner") {
      const cs = buildCircleSpinnerCourse(this.path);
      this.courseRoot.add(cs.group);
      this.circleSpinner = cs;
    }
    if (config.courseMode === "double-spinner") {
      const ds = buildDoubleSpinnerArena(this.path);
      this.courseRoot.add(ds.group);
      this.doubleSpinner = ds;
    }
    if (config.courseMode === "maze-rush") {
      const mr = buildMazeRushCourse(this.path);
      this.courseRoot.add(mr.group);
      this.mazeRush = mr;
    }
    if (config.courseMode === "supermarket-sweep") {
      const sm = buildSupermarketSweepCourse(this.path);
      this.courseRoot.add(sm.group);
      this.supermarketSweep = sm;
    }
    if (config.courseMode === "block-climb") {
      const bc = buildBlockClimbCourse(this.path);
      this.courseRoot.add(bc.group);
      this.blockClimb = bc;
    }
    if (config.courseMode === "river-boat") {
      const rb = buildRiverBoatCourse(this.path, config.clockOrigin ?? this.startedAtMs);
      this.courseRoot.add(rb.group);
      this.riverBoat = rb;
      // Register the boats with the platform-ride system.
      for (const p of rb.platforms) this.platforms.push(p);
      this.platformStates = this.platforms.map(() => null);
    }
    if (config.courseMode === "train-roof") {
      const trd = buildTrainRoofCourse(this.path);
      this.courseRoot.add(trd.group);
      this.trainRoof = trd;
    }
    if (config.courseMode === "space-ship") {
      const ss = buildSpaceShipCourse(this.path, config.clockOrigin ?? this.startedAtMs);
      this.courseRoot.add(ss.group);
      this.spaceShip = ss;
      // Black space sky + clear out any pack-theme fog so the distant
      // starfield + planets read cleanly.
      this.scene.scene.background = ss.skyColor;
      this.scene.scene.fog = null;
      for (const p of ss.platforms) this.platforms.push(p);
      this.platformStates = this.platforms.map(() => null);
    }
    if (config.courseMode === "hit-parade") {
      const cannons = buildHitParadeCannons(this.path, config.courseLength, config.clockOrigin);
      this.courseRoot.add(cannons.group);
      this.hitParadeTick = cannons.tick;
      // Moving start platform removed from Level 1 — kept available via
      // buildHitParadeStartPlatform for reuse on later levels. To bring it
      // back, uncomment + restore the gap range in Path.buildHitParade.
      void buildHitParadeStartPlatform;
    }

    this.player = new Player(config.avatar);
    this.player.movementMode = config.movementMode ?? "free";
    if (config.gravityOverride != null) this.player.gravityOverride = config.gravityOverride;
    this.player.path = this.path;
    const u = config.upgrades ?? {};
    this.player.doubleJumpUnlocked = false;
    this.coinMagnetRadius = 0.7 + upgradeEffect.coinMagnetRadius(u);
    this.coinPickupValue = Math.max(1, Math.floor(config.coinPickupValue ?? 1));
    this.quizCorrectCoins = Math.max(1, Math.floor(config.quizCorrectCoins ?? 5));
    this.quizBonusCoins = upgradeEffect.quizBonusCoins(u);
    this.winnerChestCoins = upgradeEffect.winnerChestCoins(u);
    this.extraInvulnerableMs = upgradeEffect.safetyBubbleMs(u);
    if (config.mySlotIdx !== undefined) {
      this.player.slotOffset = computeSlotOffset(config.mySlotIdx);
    }
    this.courseRoot.add(this.player.group);
    // Push the starting line ~5m up the track so the line-up doesn't sit
    // right on the lip of the course (people were spawning on the edge).
    const START_OFFSET = 5;
    // Safety net: if the host code accidentally re-creates this Game mid-
    // race (e.g. multiplayer config-identity flip from a player roster
    // broadcast), the caller can pass resumeFromS so we don't snap the
    // player back to s=0. We hold the resolved value and apply it after
    // runState is initialised below.
    const resumeS = Math.max(0, Math.min(this.path.length, config.resumeFromS ?? 0));
    let startS = resumeS > START_OFFSET ? resumeS : START_OFFSET;
    let startLane = 0;
    // Survival arena: ignore the linear START_OFFSET and place players on
    // a ring of solid tiles around the central pillar, indexed by slotIdx
    // so peers spread out instead of stacking on top of each other.
    if (this.doubleSpinner) {
      const totalSlots = Math.max(1, (config.remotePlayers?.length ?? 0) + 1);
      const slot = this.doubleSpinner.spawnSlot(config.mySlotIdx ?? 0, totalSlots);
      startS = slot.s;
      startLane = slot.laneOffset;
      this.player.laneOffset = startLane;
    }
    this.player.s = startS;
    this.spawnAtS = startS;
    this.player.update(0, performance.now());

    // Spawn ghosts for remote players.
    if (config.remotePlayers) {
      const totalSlots = Math.max(1, (config.remotePlayers.length ?? 0) + 1);
      for (const rp of config.remotePlayers) {
        const g = new Ghost(rp.id, rp.displayName, rp.avatarConfig, rp.slotIdx, this.path);
        // Survival arena: ring-of-tiles spawn matching the local player.
        if (this.doubleSpinner) {
          const slot = this.doubleSpinner.spawnSlot(rp.slotIdx, totalSlots);
          g.s = slot.s;
          g.targetS = slot.s;
        } else {
          g.s = START_OFFSET;
          g.targetS = START_OFFSET;
        }
        this.ghosts.set(rp.id, g);
        this.courseRoot.add(g.group);
      }
    }
    const plan = buildSpawnPlan({
      seed: config.seed,
      courseLength: this.path.length,
      objectsCount: config.objectsCount,
      coinsCount: config.coinsCount,
      words: config.words,
      questionsCount: config.questionsCount,
      path: this.path,
      mode: config.courseMode ?? "default",
      iceCrackPlan: this.iceCrack
        ? { gatePositions: this.iceCrack.gatePositions, spinnerPositions: this.iceCrack.spinnerPositions }
        : undefined,
      doorDashPlan: this.doorDash
        ? { gatePositions: this.doorDash.gatePositions, spinnerPositions: this.doorDash.spinnerPositions }
        : undefined,
      rollingTubesPlan: this.rollingTubes
        ? { gatePositions: this.rollingTubes.gatePositions }
        : undefined,
      circleSpinnerPlan: this.circleSpinner
        ? { gatePositions: this.circleSpinner.gatePositions }
        : undefined,
      mazeRushPlan: this.mazeRush
        ? { gatePositions: this.mazeRush.gatePositions }
        : undefined,
      spaceShipPlan: this.spaceShip
        ? { gatePositions: this.spaceShip.gatePositions }
        : undefined,
      supermarketSweepPlan: this.supermarketSweep
        ? { gatePositions: this.supermarketSweep.gatePositions }
        : undefined,
      blockClimbPlan: this.blockClimb
        ? { gatePositions: this.blockClimb.gatePositions }
        : undefined,
      riverBoatPlan: this.riverBoat
        ? { gatePositions: this.riverBoat.gatePositions }
        : undefined,
      trainRoofPlan: this.trainRoof
        ? { gatePositions: this.trainRoof.gatePositions }
        : undefined,
    });
    this.obstacles = plan.obstacles;
    this.coins = plan.coins;
    this.gates = plan.gates;

    for (const o of this.obstacles) {
      this.courseRoot.add(o.mesh);
      if (isFallingLog(o)) {
        o.onImpact = () => this.shake(performance.now(), 360, 0.5);
      }
    }
    for (const c of this.coins) {
      const m = buildCoinMesh(c, this.path);
      this.courseRoot.add(m);
      this.coinMeshes.set(c.id, m);
    }
    for (const g of this.gates) {
      const m = buildGateMesh(g, this.path);
      this.courseRoot.add(m);
      this.gateMeshes.set(g.id, m);
    }
    this.rebuildSupportMeshes();
    // Fall Guys-style plastic pass — runs once after every level mesh
    // has been added so we catch every surface in one walk.
    this.scene.applyPlasticPass();

    this.runState = {
      hearts: RUNNER.startingHearts,
      stumbles: 0,
      coins: 0,
      score: 0,
      startedAt: performance.now(),
      finishedAt: null,
      distance: startS,
      paused: !!config.startPaused,
      speed: 0,
      invulnerableUntil: 0,
    };
    if (config.resumeLastGateS != null && config.resumeLastGateS > 0) {
      this.fallRespawnAtS = Math.max(0, Math.min(this.path.length, config.resumeLastGateS));
    }
    // One-shot start snap. The per-frame resolveStandableSurface() has a
    // "don't catch surfaces more than 0.34m above my feet" cull (so a
    // mid-air descent doesn't yank onto a platform). At init though, the
    // player's feet are at path height (~0.18) but the spawn surface
    // (e.g. space-ship start island deck) can be at world y=0.73 —
    // 0.55m higher — and the cull would reject it. So we do an
    // unrestricted topmost-hit raycast here, JUST for the spawn snap.
    this.snapPlayerToTopmostSupportAtSpawn();
    this.player.update(0, performance.now());

    this.input = new Input(canvas, {
      onLeft: () => {},
      onRight: () => {},
      onDodgeLeft: () => {},
      onDodgeRight: () => {},
      onMoveAxis: (axis) => {
        this.moveAxis = axis;
      },
      onMoveVector: (x, y) => {
        this.moveAxis = x;
        this.forwardAxis = y;
      },
      onLookVector: (x, y) => {
        this.lookAxisX = x;
        this.lookAxisY = y;
      },
      onJump: () => {
        const now = performance.now();
        // Per-level jump cooldown stops players from machine-gun spamming the
        // jump to bunny-hop / climb past sections and cheat the level.
        if (now - this.lastJumpAt < this.jumpCooldownMs()) return;
        const before = this.player.jumpsLeft;
        this.player.jump(now);
        if (this.player.jumpsLeft < before) {
          sfx.jump();
          this.lastJumpAt = now; // start the cooldown only on a real jump
        }
      },
      onPause: () => this.togglePause(),
    }, {
      movementSensitivity: config.movementSensitivity,
      cameraSensitivity: config.cameraSensitivity,
      controllerDeadzone: config.controllerDeadzone,
    });

    this.handleResize();
    window.addEventListener("resize", this.handleResize);

    // Spectator-orbit drag handler. Only listens while the run is over
    // and we're spectating another player — otherwise pointer events on
    // the canvas continue to feed the joystick / jump system.
    let spectDragging = false;
    let spectLastX = 0;
    let spectLastY = 0;
    let spectPointerId = -1;
    const onSpectDown = (e: PointerEvent) => {
      if (!this.isSpectating()) return;
      spectDragging = true;
      spectLastX = e.clientX;
      spectLastY = e.clientY;
      spectPointerId = e.pointerId;
      try { this.canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    };
    const onSpectMove = (e: PointerEvent) => {
      if (!spectDragging || e.pointerId !== spectPointerId) return;
      const dx = e.clientX - spectLastX;
      const dy = e.clientY - spectLastY;
      spectLastX = e.clientX;
      spectLastY = e.clientY;
      // 0.005 rad/px feels natural on touch + mouse. Y axis inverted so
      // dragging up tilts the camera up.
      this.rotateSpectatorCamera(dx * 0.006, -dy * 0.004);
    };
    const onSpectUp = (e: PointerEvent) => {
      if (e.pointerId !== spectPointerId) return;
      spectDragging = false;
      spectPointerId = -1;
      try { this.canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    };
    this.canvas.addEventListener("pointerdown", onSpectDown);
    this.canvas.addEventListener("pointermove", onSpectMove);
    this.canvas.addEventListener("pointerup", onSpectUp);
    this.canvas.addEventListener("pointercancel", onSpectUp);
    this.cleanupCanvasListeners = () => {
      this.canvas.removeEventListener("pointerdown", onSpectDown);
      this.canvas.removeEventListener("pointermove", onSpectMove);
      this.canvas.removeEventListener("pointerup", onSpectUp);
      this.canvas.removeEventListener("pointercancel", onSpectUp);
    };
    // The browser animates the fullscreen transition over ~250ms — the
    // single `resize` event during the transition is often fired with
    // the OLD canvas size, so the renderer's drawing buffer ends up
    // stretched. Hook both the standard + webkit fullscreenchange
    // events AND fire a few delayed resizes to catch the final size
    // after the animation settles.
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange as EventListener);
    // Also observe the canvas element directly — the most reliable
    // signal for "the rendered size actually changed", and fires on
    // CSS / container size shifts that don't bubble a window resize.
    if (typeof ResizeObserver !== "undefined") {
      this.canvasResizeObserver = new ResizeObserver(() => this.handleResize());
      this.canvasResizeObserver.observe(this.canvas);
    }
    this.lastFrame = performance.now();
    if (config.startCameraFlyover) this.flyoverStartedAt = this.lastFrame;
    this.tick(this.lastFrame);
  }

  /** Kick off the survival arena's "BATTLE ROYALE" intro: the 3D banner
   *  animation plus the epic echo announcement. No-op on other levels. */
  startSurvivalIntro() {
    if (!this.doubleSpinner) return;
    this.doubleSpinner.triggerIntro();
    playBattleRoyaleAnnounce();
  }

  /** Yaw the 3D "BATTLE ROYALE" title to face the chase camera while it's
   *  visible. The arena sits at world x=arenaL, z=arenaS and courseRoot only
   *  shifts Y, so local yaw == world yaw. */
  private billboardSurvivalIntro() {
    if (!this.doubleSpinner) return;
    const intro = this.doubleSpinner.introObject;
    if (!intro.visible) return;
    const cam = this.scene.camera.position;
    const c = this.doubleSpinner.arenaCenter;
    intro.rotation.y = Math.atan2(cam.x - c.l, cam.z - c.s);
  }

  private ensureCountdownGroup() {
    if (this.countdownGroup) return;
    const group = new THREE.Group();
    group.visible = false;

    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffd93d,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      depthTest: false,
    });
    const ringA = new THREE.Mesh(new THREE.TorusGeometry(2.55, 0.055, 8, 80), ringMat.clone());
    const ringB = new THREE.Mesh(new THREE.TorusGeometry(1.95, 0.035, 8, 80), ringMat.clone());
    ringA.position.z = -0.12;
    ringB.position.z = -0.08;
    group.add(ringA, ringB);
    this.countdownRings = [ringA, ringB];

    // 3D extruded text — same family as the BATTLE ROYALE title. Geometry is
    // swapped per beat in setCountdownLabel(); the material is shared.
    if (!Game.countdownFont) {
      Game.countdownFont = new Font(helvetikerBold as unknown as ConstructorParameters<typeof Font>[0]);
    }
    const mat = new THREE.MeshStandardMaterial({
      color: 0xfff1b0,
      metalness: 0.3,
      roughness: 0.3,
      emissive: 0xffb020,
      emissiveIntensity: 0.45,
      transparent: true,
      opacity: 1,
      // Render on top of the world so the number reads clearly as it rushes
      // toward and through the camera.
      depthTest: false,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(this.buildCountdownGeometry("3"), mat);
    mesh.renderOrder = 999;
    group.add(mesh);

    this.countdownTextMesh = mesh;
    this.countdownTextMat = mat;
    this.countdownGroup = group;
    // Parented to the SCENE root (not courseRoot) so we can place it directly
    // in camera space for the fly-through.
    this.scene.scene.add(group);
  }

  /** Build a centred 3D geometry for a countdown label (digit or "GO!"). */
  private buildCountdownGeometry(label: string): TextGeometry {
    const isGo = label === "GO!";
    const geo = new TextGeometry(label, {
      font: Game.countdownFont!,
      size: isGo ? 2.4 : 3.0,
      depth: isGo ? 0.7 : 0.9,
      curveSegments: 6,
      bevelEnabled: true,
      bevelThickness: 0.12,
      bevelSize: 0.09,
      bevelSegments: 2,
    });
    geo.center();
    return geo;
  }

  private canvasResizeObserver: ResizeObserver | null = null;
  private fullscreenResizeTimers: number[] = [];
  private cleanupCanvasListeners: (() => void) | null = null;

  private handleResize = () => {
    const r = this.canvas.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      this.scene.resize(r.width, r.height);
    }
  };

  /** Browser fullscreen transitions animate the canvas size over a
   *  few hundred ms. Fire the resize once now and then again on a
   *  schedule so the renderer's drawing buffer matches the final
   *  display size after the animation completes. Without these
   *  delayed re-snaps the canvas ends up stretched or squashed. */
  private handleFullscreenChange = () => {
    for (const id of this.fullscreenResizeTimers) clearTimeout(id);
    this.fullscreenResizeTimers = [];
    const fire = (delay: number) => {
      this.fullscreenResizeTimers.push(
        window.setTimeout(() => this.handleResize(), delay),
      );
    };
    // Immediate (current frame), short (mid-transition), settle (after).
    this.handleResize();
    fire(60);
    fire(160);
    fire(350);
  };

  togglePause() {
    this.runState.paused = !this.runState.paused;
    this.emitState();
  }

  setMoveVector(x: number, y: number) {
    const fwd = THREE.MathUtils.clamp(y, -1, 1);
    // Forward-input gate removed — the player's joystick value flows
    // through immediately after a resume so the post-question stall is
    // gone. (The gate caused a ~half-second block while the player held
    // forward through the question.)
    this.forwardInputGate = false;
    this.moveAxis = THREE.MathUtils.clamp(x, -1, 1);
    this.forwardAxis = fwd;
    this.input.setMoveVector(this.moveAxis, this.forwardAxis);
  }

  jump() {
    this.input.jump();
  }

  isAirborne() {
    return this.player.y > 0.18 || this.player.state === "jump";
  }

  /** Minimum ms between jumps for the current level. Higher on the levels
   *  where jump-spam lets players cheat past sections; a small floor on the
   *  rest just kills machine-gun spamming without hurting normal play. */
  private jumpCooldownMs(): number {
    const m = this.config.courseMode;
    if (m === "river-boat") return 1100;
    if (m === "block-climb" || m === "train-roof" || m === "double-spinner") return 750;
    return 380;
  }

  /** Jump-cooldown progress for the HUD button, 0..1. 1 = just jumped
   *  (fully on cooldown), 0 = ready. */
  getJumpCooldown01(): number {
    const cd = this.jumpCooldownMs();
    if (cd <= 0) return 0;
    const elapsed = performance.now() - this.lastJumpAt;
    if (elapsed >= cd) return 0;
    return Math.max(0, Math.min(1, 1 - elapsed / cd));
  }

  /** Read-only snapshot used by the sandbox debug overlay. Returns
   *  whatever the UI needs to render without exposing the engine guts. */
  debugSnapshot(): {
    grounded: boolean;
    pos: { x: number; y: number; z: number };
    vy: number;
    supportCount: number;
  } {
    const p = this.player.group.position;
    return {
      grounded: this.player.isGroundedOnSurface(),
      pos: { x: p.x, y: p.y, z: p.z },
      vy: this.player.vy,
      supportCount: this.supportMeshes.length,
    };
  }

  /** Enter the after-finish spectator: hide the local player and watch the
   *  best available ghost. Returns the id of the ghost now being watched
   *  (or null if there's nobody to watch). */
  enterSpectator(): string | null {
    this.player.group.visible = false;
    const target = this.pickSpectateGhost();
    this.spectateTargetId = target?.id ?? null;
    return this.spectateTargetId;
  }

  /** Cycle to the prev (-1) or next (+1) eligible ghost in the watch list.
   *  Returns the new target id (or null if no other ghost is available). */
  cycleSpectator(dir: 1 | -1): string | null {
    const eligible = this.eligibleSpectatorGhosts();
    if (eligible.length === 0) {
      this.spectateTargetId = null;
      return null;
    }
    const currentIdx = eligible.findIndex((g) => g.id === this.spectateTargetId);
    const nextIdx = currentIdx < 0
      ? 0
      : (currentIdx + dir + eligible.length) % eligible.length;
    this.spectateTargetId = eligible[nextIdx]!.id;
    return this.spectateTargetId;
  }

  /** True while the local player is in after-finish spectator mode. */
  isSpectating(): boolean {
    return this.spectateTargetId != null;
  }

  /** Display name fetched from the ghost we're watching (id-based) — set
   *  by RacePlay via setRemotePosition; we re-look the latest position.
   *  Returns the ghost's display name set in its constructor, or null. */
  getSpectatorTargetId(): string | null {
    return this.spectateTargetId;
  }

  private eligibleSpectatorGhosts(): Ghost[] {
    const list: Ghost[] = [];
    for (const g of this.ghosts.values()) {
      if (this.ghostStates.get(g.id) === "finished") continue;
      list.push(g);
    }
    // Stable order by id for deterministic prev/next.
    list.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    return list;
  }

  updateSettings(settings: Pick<GameSettings, "movementMode" | "movementSensitivity" | "cameraSensitivity" | "controllerDeadzone"> & Partial<Pick<GameSettings, "assistMode">>) {
    this.player.movementMode = settings.movementMode;
    this.input.setOptions({
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
    });
    if (settings.assistMode !== undefined) {
      this.assistOverride = settings.assistMode;
    }
  }

  setPaused(paused: boolean) {
    const wasPaused = this.runState.paused;
    this.runState.paused = paused;
    if (wasPaused && !paused && this.runState.distance < 0.05) {
      this.runState.startedAt = performance.now();
    }
    this.emitState();
  }

  private emitState() {
    this.onEvent({ type: "state", state: { ...this.runState } });
  }

  private currentSpeed(now = performance.now(), alignment = 1): number {
    // Per-level fixed speed (set by LessonPlay from lesson order index).
    // No correct-answer speed surge — the player runs steadily.
    const base = (this.config.runSpeed ?? RUNNER.baseSpeed) * 0.72;
    // Post-question forward gate removed — the input axes are zeroed in
    // suspend() and resume() once, and after that we trust whatever the
    // player is sending. Half-second stalls after a correct answer are
    // gone. Field still referenced elsewhere for type compat but never
    // forces a stop.
    void this.forwardInputGate;
    void this.forwardInputGateSince;
    const input = this.localMoveForward;
    if (now < this.knockSlowUntil) return base * input * alignment * 0.12;
    return base * input * alignment;
  }

  private tick = (now: number) => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.tick);
    // 30fps cap on low tier: skip this rAF frame if too little time has passed
    // (don't touch lastFrame so dt accumulates to the next rendered frame).
    if (this.frameMinMs > 0 && now - this.lastFrame < this.frameMinMs * 0.85) return;
    const rawDt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.lastDt = rawDt;
    if (this.dying) {
      const t = Math.min(1, (now - this.deathStartedAt) / 600);
      this.timeScale = 1 - 0.85 * easeOutCubic(t);
      const dt = rawDt * this.timeScale;
      this.player.path = null; // free pieces from path during crumble
      this.player.update(dt, now);
      for (const g of this.ghosts.values()) g.update(rawDt);
      const spectateTarget = this.config.spectateAfterDeath && now - this.deathStartedAt > 2600
        ? this.pickSpectateGhost()
        : null;
      if (spectateTarget) this.applySpectatorCamera(spectateTarget, now);
      else this.applyDeathCamera(now);
      this.scene.render();
      if (now - this.deathStartedAt > 1500 && this.pendingDeath) {
        this.onEvent({ type: "death", outcome: this.pendingDeath });
        this.pendingDeath = null;
      }
      // Shark-bite respawn: the dying branch early-returns before
      // handleRiverBoatFishCollision can run, so route the respawn
      // here. After 1.4s of slow-mo crumble, teleport back to the
      // last checkpoint (or start dock).
      if (this.sharkDeathAt && now - this.sharkDeathAt > 1400) {
        this.sharkDeathAt = null;
        this.dying = false;
        this.timeScale = 1;
        this.respawnFromFreefall(now);
      }
      return;
    }
    if (!this.runState.paused && this.runState.finishedAt == null) {
      this.update(rawDt, now);
    } else if (this.runState.paused && this.runState.finishedAt == null) {
      // Paused but not finished — covers BOTH the solo flyover AND
      // the multiplayer countdown (which doesn't set flyoverStartedAt).
      // Animate the world + keep the player snapped to spawn so they
      // don't float / sink before the race actually starts.
      this.tickWorldOnly(rawDt, now);
      this.applyCamera(this.runState.distance, now);
    } else if (this.spectateTargetId) {
      // After-finish spectator: keep ticking ghosts so we can follow them,
      // and aim the camera at the chosen target.
      for (const g of this.ghosts.values()) g.update(rawDt);
      const target = this.ghosts.get(this.spectateTargetId);
      if (target) {
        this.applySpectatorCamera(target, now);
      } else {
        // Target gone (left the room). Try to find a new one.
        const next = this.pickSpectateGhost();
        this.spectateTargetId = next?.id ?? null;
        if (next) this.applySpectatorCamera(next, now);
        else this.applyCamera(this.runState.distance, now);
      }
    } else {
      this.applyCamera(this.runState.distance, now);
    }
    this.updateCountdown(now);
    this.scene.render();
  };

  /** Animate the world without running player physics or collisions. Used
   *  during the pre-race flyover and countdown so balls bounce, platforms
   *  move, cannons fire, and spinners spin while the player is just watching. */
  private tickWorldOnly(dt: number, now: number) {
    const z = this.runState.distance;
    const livePlatforms = this.platformStates.filter((p): p is NonNullable<typeof p> => p != null);
    const clockMs = Date.now() - (this.config.clockOrigin ?? this.startedAtMs);
    for (const o of this.obstacles) {
      const oz = o.getZ();
      // Animate a wider window during flyover so balls visible from the
      // overhead camera don't pop in only when the player gets close.
      if (oz < z - 8 || oz > z + this.path.length) continue;
      o.update({ dt, now, playerZ: z, clockMs, peers: this.obstacles, platforms: livePlatforms });
    }
    this.updateDynamicObstacleContacts();
    this.hitParadeTick?.(now);
    if (this.doorDash) this.doorDash.tick(clockMs, now);
    if (this.iceCrack) {
      this.iceCrack.tick(clockMs, []);
    }
    // Tick every course-specific obstacle / animation system during the
    // flyover so the level isn't a static still-life: rolling tubes
    // scroll, swinging axes swing, circle spinners spin, maze-rush
    // pendulums sway, space-ship moving platforms drift, asteroids drift.
    this.rollingTubes?.tick(clockMs);
    this.circleSpinner?.tick(clockMs);
    // Survival arena: keep the tile-drop schedule FROZEN during the pre-race
    // flyover / countdown (no tile lost before GO!), but still run the
    // presentation pass so the crowd cheers + the BATTLE ROYALE title plays.
    this.doubleSpinner?.tickIdle(dt);
    this.billboardSurvivalIntro();
    this.mazeRush?.tick(clockMs);
    this.spaceShip?.tick(clockMs, dt);
    this.blockClimb?.tick(clockMs, dt);
    this.snowfallTick?.(dt, 0, 0, 0);
    this.updatePlatforms(dt, now);
    // Keep the player visually planted on the spawn surface during the
    // PRE-RACE pause. This used to be gated on the flyover window, but
    // battle mode skips the flyover entirely — so the multiplayer
    // countdown couldn't re-snap and the player floated at start.
    //
    // We now track an explicit "haven't started racing yet" condition:
    //   • flyover still active (solo)              OR
    //   • the player hasn't moved from spawn yet   (battle / no-flyover)
    // Either way we re-snap. Once the race is genuinely live (the
    // player has made forward progress) we stop — otherwise the
    // question-gate pause would visually re-drop the player every
    // time they answer.
    const inFlyover =
      this.flyoverStartedAt && (now - this.flyoverStartedAt < this.flyoverDurationMs);
    const hasMovedSinceSpawn = this.runState.distance > this.spawnAtS + 0.5;
    if (inFlyover || !hasMovedSinceSpawn) {
      this.snapPlayerToTopmostSupportAtSpawn();
      this.player.update(0, now);
    }
  }

  private update(dt: number, now: number) {
    // Assist mode (auto-run + auto-jump for very young players): override
    // the forward input so the player marches forward regardless of joystick.
    // Lateral input still flows through normally so the kid can steer.
    // Read live so the pause-menu toggle takes effect immediately.
    const assistOn = this.assistOverride ?? this.config.assistMode;
    if (assistOn && !this.runState.paused) {
      if (this.forwardAxis < 0.95) this.forwardAxis = 1;
      // Auto-jump: scan ahead for any non-jump-passable obstacle in the
      // player's current lane band. Trigger a jump while approaching it.
      this.maybeAutoJump(now);
    }
    const playerKnockedDown = this.player.state === "hit";
    // Airborne means literally in the air — not just elevated by a moving
    // platform or other standable surface. The standable-support flag
    // disambiguates "standing on a platform 0.55m up" from "mid-jump".
    const isJumpState = this.player.state === "jump";
    const supported = this.player.isGroundedOnSurface();
    const playerAirborne = isJumpState || (this.player.y > 0.16 && !supported);
    if (playerAirborne && !this.wasAirborne) this.jumpCarrySpeed = this.runState.speed;
    this.updateDesiredPlayerFacing(playerAirborne);
    // Air control: keep partial steering authority mid-jump so holding forward
    // from a standing jump nudges the player forward (Fall Guys-style) instead
    // of going straight up.
    const AIR_CONTROL = this.config.airControl ?? 0.55;
    const controlScale = playerKnockedDown ? 0 : playerAirborne ? AIR_CONTROL : 1;
    const moveAlignment = this.player.getMovementAlignment();
    if (playerKnockedDown) {
      this.runState.speed = this.playerKnockbackSpeed;
      this.playerKnockbackSpeed *= Math.exp(-1.85 * dt);
      if (Math.abs(this.playerKnockbackSpeed) < 0.05) this.playerKnockbackSpeed = 0;
    } else {
      this.playerKnockbackSpeed = 0;
      if (playerAirborne) {
        // Forward gets full input authority mid-air so a running jump
        // actually carries (and a standing jump-forward visibly moves
        // the player). Take whichever is greater: the launch momentum
        // we captured when the jump started, or the current
        // input-driven ground-equivalent speed. Apply directly so
        // forward feels as responsive as lateral steering does.
        const inputSpeed = this.currentSpeed(now, moveAlignment);
        this.runState.speed = Math.max(this.jumpCarrySpeed, inputSpeed);
      } else {
        this.runState.speed = this.currentSpeed(now, moveAlignment);
      }
      // River-boat: walking through water slows the player to a
      // wade. They CAN still move, but at half speed — perfect setup
      // for the shark to catch them.
      if (this.isPlayerInRiverWater()) {
        this.runState.speed *= 0.5;
      }
      // ── Slippery slide on rolling-tubes down-ramps ──
      // The player AUTO-RUNS forward at a constant slide speed; the
      // joystick gives ~30% lateral control (so they can still aim for
      // a doorway) but their forward speed is locked, regardless of
      // whether they're pushing forward / backward / nothing.
      const rt = this.path.rollingTubes;
      if (rt) {
        const s = this.runState.distance;
        const slide = rt.tubes.find((t) => t.slide && s >= t.start && s <= t.end);
        if (slide) {
          const SLIDE_SPEED = (this.config.runSpeed ?? RUNNER.baseSpeed) * 1.15;
          this.runState.speed = SLIDE_SPEED;
        }
      }
    }
    // No invisible wall at s=0 — the start of each level used to clamp
    // distance to ≥ 0, blocking backwards movement. In sandbox mode
    // the player can walk off the back of the start platform and fall
    // naturally (no support → gravity), same as any other edge.
    const prevDistance = this.runState.distance;
    this.runState.distance = Math.min(
      this.runState.distance + this.runState.speed * dt,
      this.path.length,
    );
    // Rolling-tubes (pyramid) — block forward progress into a step
    // taller than the player can step onto. The player's FEET need
    // to be at or near the next step's top to climb; otherwise they
    // bonk into the side wall and have to jump.
    if (this.path.rollingTubes && this.runState.speed > 0) {
      const rt = this.path.rollingTubes;
      const STEP_UP_BUDGET = 0.4; // auto-climb threshold (knee-high)
      // Player's foot world Y = path heightAt(prev) + player.y.
      // For pyramid level the heightAt returns the CURRENT step's Y so
      // the foot is exactly at here.y when grounded. When jumping, the
      // foot rises by player.y above that.
      const prevHeight = this.path.heightAt(prevDistance);
      const footWorldY = prevHeight + this.player.y;
      for (let i = 0; i < rt.platforms.length - 1; i++) {
        const here = rt.platforms[i]!;
        const next = rt.platforms[i + 1]!;
        const rise = next.y - here.y;
        if (rise <= STEP_UP_BUDGET) continue;
        // Player crossed the boundary into a taller step this frame?
        if (prevDistance < next.start && this.runState.distance >= next.start) {
          // Need the foot to be within STEP_UP_BUDGET of the next
          // platform top. Otherwise: bonk back.
          if (footWorldY < next.y - STEP_UP_BUDGET) {
            this.runState.distance = next.start - 0.05;
            this.runState.speed = 0;
            this.player.s = this.runState.distance;
            break;
          }
        }
      }
    }
    const z = this.runState.distance;
    this.player.s = z;
    if (z > this.highWaterS) this.highWaterS = z;
    this.player.steer(this.localMoveLateral * moveAlignment * controlScale, dt);
    // Struggle input is sampled even during input-lock so the player can
    // mash-opposite to recover from a knockdown faster (Fall Guys style).
    this.player.struggleInput = this.moveAxis;
    // On slippery slides, the joystick has less authority — the kid
    // can still aim laterally but they're locked into the forward
    // direction by the slope momentum.
    let intensityScale = controlScale;
    const rtSlide = this.path.rollingTubes?.tubes.find(
      (t) => t.slide && this.runState.distance >= t.start && this.runState.distance <= t.end,
    );
    if (rtSlide) intensityScale *= 0.35;
    this.player.setMoveIntensity(Math.min(1, Math.hypot(this.moveAxis, this.forwardAxis)) * moveAlignment * intensityScale);
    this.player.update(dt, now);
    this.applyMazeRushRenderWarp();
    this.wasAirborne = (this.player.y > 0.16 && !this.player.isGroundedOnSurface()) || this.player.state === "jump";
    if (!this.wasAirborne) this.jumpCarrySpeed = 0;
    this.resolveStandableSurface();
    // Sandbox mode: no auto-respawn, no death animation. The player
    // just falls via gravity in player.update() while the camera keeps
    // following. They can land on lower platforms naturally via the
    // foot-raycast snap. The watchdog only runs if the flag is on.
    if (SANDBOX_CONFIG.enableAutoRespawnOnFall) {
      // Robust backstop: if the player has dropped far below any possible
      // surface, respawn IMMEDIATELY regardless of velocity. Covers fake-fall
      // states where vy stays ~0 (maze-rush between-lane fall) and any case
      // that slips past the timer below — the player can never be lost in the
      // void forever.
      const footY = this.player.group.position.y + this.player.y;
      if (!this.dying && footY < -28) {
        this.respawnFromFreefall(now);
        return;
      }
      // While dying (shark bite, crumble animations) the freefall watchdog
      // must not fire — the death sequence owns the respawn timing.
      if (this.dying) {
        this.unsupportedSince = null;
      } else if (this.hasStandableSupport || this.player.vy >= 0) {
        this.unsupportedSince = null;
      } else if (this.unsupportedSince == null) {
        this.unsupportedSince = now;
      }
      if (this.unsupportedSince != null && now - this.unsupportedSince > 2000) {
        this.respawnFromFreefall(now);
        return;
      }
    } else {
      this.unsupportedSince = null;
    }
    this.applyCamera(z, now);

    // Coin pickups (radius bumped by Coin Magnet upgrade).
    for (const c of this.coins) {
      if (c.collected) continue;
      const inLane = this.player.movementMode === "free"
        ? this.player.overlapsLane(c.lane, 0.85)
        : c.lane === this.player.laneTarget;
      const inRadius = Math.abs(c.z - z) < this.coinMagnetRadius;
      const magnet = this.coinMagnetRadius > 0.7;
      const laneOk = inLane || (magnet && this.player.overlapsLane(c.lane, 2.4));
      if (inRadius && laneOk) {
        c.collected = true;
        const m = this.coinMeshes.get(c.id);
        if (m) m.visible = false;
        this.runState.coins += this.coinPickupValue;
        this.runState.score += 10;
        sfx.coin();
        this.onEvent({ type: "coin", value: this.coinPickupValue });
        this.emitState();
      }
    }

    // Update obstacles within visible window. Platforms from the previous
    // frame are good enough (~16ms stale) for ball collision purposes.
    const livePlatforms = this.platformStates.filter((p): p is NonNullable<typeof p> => p != null);
    // Shared wall-clock since the race started. In multiplayer all clients
    // share clockOrigin (set to state.countdownStartedAt) so any obstacle
    // computing its state from clockMs renders identically on every device.
    const clockMs = Date.now() - (this.config.clockOrigin ?? this.startedAtMs);
    for (const o of this.obstacles) {
      const oz = o.getZ();
      // Bouncing balls must keep ticking even when out of the normal
      // visibility window — otherwise after a ball rolls past the player
      // it freezes forever (never reaches `airY < -14` → never recycles)
      // and the upper half of the level runs out of fresh balls.
      const alwaysTick = o.kind === "bouncing-ball" || o.kind === "snowball";
      if (!alwaysTick && (oz < z - 8 || oz > z + 60)) continue;
      o.update({ dt, now, playerZ: z, clockMs, peers: this.obstacles, platforms: livePlatforms });
    }
    this.updateDynamicObstacleContacts();
    if (this.resolvePhysicalObstacleContacts(now)) return;
    if (this.path.length - z < 80) this.world.update(now);
    this.hitParadeTick?.(now);
    if (this.iceCrack) {
      // Local player + every visible ghost participate in tile cracking.
      // The decor module uses the stepper id to enforce: "first stepper just
      // cracks; a different stepper, or 2s+ on the same tile, makes it fall."
      const steppers: { id: string; s: number; l: number }[] = [];
      // Only count the local player as standing on ice when they're actually
      // grounded. Jumping over a tile must not crack it.
      const localGrounded = this.player.y < 0.18 && this.player.state !== "jump";
      if (localGrounded) {
        steppers.push({
          id: "local",
          s: this.runState.distance,
          l: this.player.laneOffset + this.player.slotOffset,
        });
      }
      for (const [gid, g] of this.ghosts) {
        steppers.push({ id: gid, s: g.s, l: g.laneOffset + g.slotOffset });
      }
      this.iceCrack.tick(clockMs, steppers);
    }
    if (this.snowfallTick) {
      const cam = this.scene.camera.position;
      this.snowfallTick(dt, cam.x, cam.y, cam.z);
    }
    if (this.doorDash) {
      this.doorDash.tick(clockMs, now);
      this.handleDoorCollisions(now);
    }
    if (this.rollingTubes) {
      this.rollingTubes.tick(clockMs);
      this.applyRollingTubePush(dt);
      this.handleRollingTubeAxeCollision(now);
      this.handleRollingTubeSpinnerCollision(now);
      this.handleRollingTubeBallCollision(now);
      // Hard forward block at every axe — even when the player avoids the
      // swinging blade laterally, the axe assembly is now a wall they
      // can't run straight through. Must jump high enough to clear it.
      const blockS = this.rollingTubes.axeForwardBlock(
        this.runState.distance,
        this.player.group.position.y,
      );
      if (blockS != null && this.runState.distance > blockS - 0.05) {
        this.runState.distance = blockS;
        this.player.s = blockS;
        this.runState.speed = Math.min(this.runState.speed, 0);
      }
    }
    if (this.circleSpinner) {
      this.circleSpinner.tick(clockMs);
      this.handleCircleSpinnerCollision(now);
    }
    if (this.trainRoof) {
      this.trainRoof.tick(clockMs);
      this.handleTrainRoofCollision(now);
    }
    if (this.doubleSpinner) {
      this.doubleSpinner.tick(clockMs);
      this.billboardSurvivalIntro();
      this.handleDoubleSpinnerCollision(now);
      // No round timer — survival runs until you fall. The last player to
      // fall wins (ranked by survival time); see reportSurvivalFall().
    }
    if (this.blockClimb) {
      this.blockClimb.tick(clockMs, dt);
      this.handleBlockClimbBallCollision(now);
    }
    if (this.mazeRush) {
      this.mazeRush.tick(clockMs);
      this.updateMazeRushLane(dt, now);
      this.handleMazeRushPendulumCollision(now);
    }
    if (this.spaceShip) {
      this.spaceShip.tick(clockMs, dt);
      this.handleSpaceShipSpinnerCollision(now);
    }
    if (this.supermarketSweep) {
      // Stepper list includes the local player + any active ghosts so
      // remote players' steps also break cracked tiles in sync.
      const steppers: { id: string; s: number; l: number }[] = [
        {
          id: "me",
          s: this.runState.distance,
          l: this.player.laneOffset + this.player.slotOffset,
        },
      ];
      for (const [gid, g] of this.ghosts) steppers.push({ id: gid, s: g.s, l: g.laneOffset });
      this.supermarketSweep.tick(clockMs, steppers);
      this.applySupermarketSlipPush(dt);
      this.handleSupermarketProductCollision(now);
    }
    if (this.riverBoat) {
      // Tell the shark where the player is + whether they're splashing
      // through water so it can switch from patrol to hunt mode.
      const ps = this.runState.distance;
      const pl = this.player.laneOffset + this.player.slotOffset;
      // The shark only hunts/bites a player who has actually splashed DOWN
      // into the water — not one cleanly jumping over the gap. Require them
      // to be at the surface (low Y) and not mid-jump, so a boat-to-boat hop
      // is always safe.
      const inWater =
        this.isPlayerInRiverWater() &&
        this.player.state !== "jump" &&
        this.player.y < 0.35;
      this.riverBoat.setSharkTarget(ps, pl, inWater);
      this.riverBoat.tick(clockMs, dt);
      this.handleRiverBoatFishCollision(now);
    }
    this.updatePlatforms(dt, now);

    // Update opponent ghosts.
    // Lift ghosts onto moving platforms so they appear standing on top
    // instead of clipping into the platform mesh. We do this BEFORE update so
    // the surfaceY change is rendered the same frame.
    for (const g of this.ghosts.values()) {
      let yLift = 0;
      for (const st of this.platformStates) {
        if (!st) continue;
        const dS = Math.abs(st.s - g.s);
        const dL = Math.abs(g.laneOffset + g.slotOffset);
        if (dS <= st.halfDepth + 0.3 && dL <= st.halfWidth + 0.3) {
          // Top of the platform minus path-ground (which is 0 in flat modes).
          yLift = Math.max(yLift, st.topY - this.path.heightAt(g.s));
        }
      }
      g.surfaceY = yLift;
      g.update(dt);
    }
    // Player ↔ Ghost bumping. Capsule-vs-capsule in (s, lateral) space.
    // Symmetric in feel: relative velocity drives the impulse, so a fast
    // player hitting a stationary ghost pushes the player sideways too. We
    // can't move the ghost (it's network-driven), so the local player takes
    // the resolution + a slight slowdown.
    // Player ↔ Ghost mutual blocking: capsule-vs-capsule in (s, lateral).
    // Resolves penetration fully on EVERY frame (no contact cooldown) so two
    // players actually block each other rather than slipping through. The
    // bump impulse still fires once per contact for the audible "ouf!" effect.
    if (this.player.movementMode === "free") {
      const myRadius = 0.42;
      const ghostRadius = 0.42;
      const minSep = myRadius + ghostRadius;
      const playerY = this.player.y;
      const edge = 5.0;
      // Effective top of an opponent's head in path-local Y space. With
      // ghost body scale ~0.28 and head crown at body-local y ≈ 2.5, the
      // crown sits ~0.7m above the path surface.
      const GHOST_HEAD_TOP = 0.7;
      for (const g of this.ghosts.values()) {
        if (this.ghostStates.get(g.id) === "finished") continue;
        const myLateral = this.player.laneOffset + this.player.slotOffset;
        const dl = myLateral - (g.laneOffset + g.slotOffset);
        const ds = this.runState.distance - g.s;
        const dist = Math.hypot(dl, ds);
        if (dist >= minSep) continue;
        // ── Land-on-head: if the player is descending (or hovering) and
        //     is above the ghost's shoulders, treat the head as a
        //     standable surface instead of bumping laterally. Lets
        //     players hop onto each other Fall-Guys style. ──
        const playerAboveHead = playerY >= GHOST_HEAD_TOP - 0.18;
        const descending = this.player.vy <= 0.1;
        if (playerAboveHead && descending) {
          // Snap onto the head crown, zero downward velocity.
          this.player.landOn(GHOST_HEAD_TOP);
          // Don't push laterally — they're balancing on top.
          continue;
        }
        // Jumping over (high above the head still clears).
        if (playerY > GHOST_HEAD_TOP + 0.5) continue;
        const pen = minSep - dist;
        const nl = dist > 0.001 ? dl / dist : (Math.random() < 0.5 ? 1 : -1);
        const ns = dist > 0.001 ? ds / dist : 0;
        // Hard separation — push us fully out along the contact normal so we
        // can't overlap. Lateral via laneOffset, forward via runState.distance.
        const shovedLateral = this.player.laneOffset + nl * pen;
        this.player.laneOffset = THREE.MathUtils.clamp(shovedLateral, -edge, edge);
        const shovedS = this.runState.distance + ns * pen;
        this.runState.distance = THREE.MathUtils.clamp(shovedS, 0, this.path.length);
        this.player.s = this.runState.distance;
        // Kill forward speed pushing into the ghost so we don't keep trying
        // to march through them every frame.
        if (ns < 0 && this.runState.speed > 0) this.runState.speed *= 0.4;
        // One-shot bump impulse + SFX gated by ghostContactUntil so the
        // crunch noise doesn't fire 60×/sec while two players stand together.
        if ((this.ghostContactUntil.get(g.id) ?? 0) <= now) {
          const relL = (g.getVelocityL() - this.player.getLateralVelocity()) * nl;
          const closingSpeed = Math.max(0, -relL);
          const punch = THREE.MathUtils.clamp(pen * 7 + closingSpeed * 0.8, 1.6, 4.5);
          this.player.applyImpulse(nl * punch, 0);
          this.ghostContactUntil.set(g.id, now + 320);
          sfx.hit();
          this.shake(now, 110, 0.08);
        }
      }
    }

    // "Jumping" for obstacle pass-through means actually airborne — not just
    // elevated because of a standable surface like a platform. Without this
    // check, standing on a platform (y ≈ 0.55) would let you "clear" every
    // obstacle that's jump-passable (spinners, crates, etc.).
    const jumping = this.player.y > 0.5 && !this.player.isGroundedOnSurface();
    const sliding = false; // slide removed
    for (const o of this.obstacles) {
      if (o.resolved) continue;
      if (o.getContact) continue;
      const oz = o.getZ();
      if (Math.abs(oz - z) > obstacleContactDepth(o)) continue;
      const laneHit = this.player.movementMode === "free"
        ? o.lanes().some((lane) => this.player.overlapsLane(lane, obstacleLaneRadius(o)))
        : o.lanes().includes(this.player.laneTarget);
      if (!laneHit) continue;
      if (o.passes({ jumping, sliding })) {
        o.resolved = true;
        continue;
      }

      const forceImpact = !!o.hasForceImpact?.();
      if (forceImpact && now < this.runState.invulnerableUntil) continue;
      this.blockPlayerAtObstacle(o);
      if (!forceImpact) return;

      this.runState.stumbles += 1;
      this.runState.score = Math.max(0, this.runState.score - 15);
      this.runState.invulnerableUntil = now + obstacleRecoveryMs(o) + this.extraInvulnerableMs;
      const obstacleLateral = obstacleCenterOffset(o);
      const playerLateral = this.player.laneOffset;
      const dir = playerLateral >= obstacleLateral ? 1 : -1;
      const impulse = obstacleImpactImpulse(o);
      const massScale = Math.sqrt(Math.max(1, o.mass ?? 3) / 3);
      this.player.knockdown(now, dir * impulse.lateral * massScale, impulse.vertical * Math.min(1.35, massScale));
      this.playerKnockbackSpeed = -impulse.forward * Math.min(1.45, massScale);
      this.knockSlowUntil = now + impulse.slowMs;
      o.applyPhysicsImpulse?.(-Math.abs(this.playerKnockbackSpeed) * 0.08, -dir * impulse.lateral * 0.14);
      o.impact?.({
        now,
        playerS: z,
        playerLaneOffset: playerLateral,
        playerForwardVelocity: this.runState.speed,
      });
      sfx.hit();
      this.shake(now, impulse.shakeMs, impulse.shake);
      this.onEvent({ type: "hit" });
      this.emitState();
      return;
    }

    // Survival mode (Level 10) has NO finish line — `path.length` is
    // only a bookkeeping value (the disc fits inside it). Skipping the
    // auto-finish here prevents the "player jumps to the back of the
    // disc and the engine thinks they finished the level" bug.
    if (z >= this.path.length && !this.doubleSpinner) {
      this.finishRun(true);
      return;
    }

    // Word gates double as checkpoints. Whether the player answers correctly
    // or not, crossing the gate stamps the respawn point so a later fall or
    // death sends them back here instead of all the way to the start.
    for (const g of this.gates) {
      if (g.triggered) continue;
      if (g.z - z < 0 && Math.abs(g.z - z) < 1.5) {
        g.triggered = true;
        this.lastCheckpointS = g.z;
        this.suspend();
        this.onEvent({
          type: "challenge",
          word: g.word,
          resolve: (correct) => this.resolveChallenge(g.word.id, correct),
        });
      }
    }

    // Coin spin — budget the nearest visible coins only.
    let coinSpinBudget = 24;
    for (const c of this.coins) {
      if (c.collected) continue;
      if (coinSpinBudget <= 0) break;
      if (c.z < z - 4 || c.z > z + 50) continue;
      const m = this.coinMeshes.get(c.id);
      if (m) m.rotation.y += dt * 4;
      coinSpinBudget--;
    }

  }

  private suspend() {
    this.suspendedSpeed = this.runState.speed;
    this.runState.paused = true;
    // Kill every trace of joystick state the moment the question gate fires.
    // Without this, a touch that was held forward when the gate triggered
    // gets re-applied the instant we resume — so the player auto-runs out
    // of the gate before the user has touched the screen.
    this.moveAxis = 0;
    this.forwardAxis = 0;
    this.localMoveForward = 0;
    this.localMoveLateral = 0;
    this.runState.speed = 0;
    this.input.setMoveVector(0, 0);
    // Same release-gate trick as resume(): require a fresh release-then-
    // press before forward motion is honoured again.
    // No forward-input gate — the joystick is zeroed once here; any
    // fresh value the player sends after resume flows straight through.
    this.forwardInputGate = false;
    this.forwardInputGateSince = 0;
    this.emitState();
  }

  private resume() {
    // Reset speed to 0 — restoring pre-pause speed caused one frame of
    // phantom forward motion before next-frame input recomputed speed.
    this.runState.speed = 0;
    this.suspendedSpeed = null;
    this.moveAxis = 0;
    this.forwardAxis = 0;
    this.localMoveForward = 0;
    this.localMoveLateral = 0;
    // Require a fresh release-then-press before auto-walking — otherwise a
    // held key / virtual joystick carries the player through the gate.
    // No forward-input gate — the joystick is zeroed once here; any
    // fresh value the player sends after resume flows straight through.
    this.forwardInputGate = false;
    this.forwardInputGateSince = 0;
    this.runState.paused = false;
    this.emitState();
  }

  private blockPlayerAtObstacle(o: ObstacleController) {
    this.runState.distance = Math.max(0, o.getZ() - obstacleBlockDistance(o));
    this.runState.speed = 0;
    this.player.s = this.runState.distance;
  }

  private updateDesiredPlayerFacing(playerAirborne = false) {
    const x = this.moveAxis;
    const y = this.forwardAxis;
    const amount = Math.min(1, Math.hypot(x, y));
    if (amount < 0.05 || this.player.state === "hit") {
      this.localMoveForward = 0;
      this.localMoveLateral = 0;
      this.player.setDesiredFacing(null, 0);
      return;
    }
    const cameraForward = _moveCameraForward.set(0, 0, -1).applyQuaternion(this.scene.camera.quaternion);
    cameraForward.y = 0;
    if (cameraForward.lengthSq() < 0.001) cameraForward.set(0, 0, 1);
    cameraForward.normalize();
    const cameraRight = _moveCameraRight.set(1, 0, 0).applyQuaternion(this.scene.camera.quaternion);
    cameraRight.y = 0;
    if (cameraRight.lengthSq() < 0.001) cameraRight.set(1, 0, 0);
    cameraRight.normalize();
    const worldDir = _moveWorldDir
      .copy(cameraForward)
      .multiplyScalar(y)
      .addScaledVector(cameraRight, x);
    if (worldDir.lengthSq() < 0.001) {
      this.localMoveForward = 0;
      this.localMoveLateral = 0;
      this.player.setDesiredFacing(null, 0);
      return;
    }
    worldDir.normalize();
    const tangent = this.path.tangentAt(this.runState.distance, _moveTan);
    const side = _moveSide.set(tangent.z, 0, -tangent.x).normalize();
    this.localMoveForward = THREE.MathUtils.clamp(worldDir.dot(tangent) * amount, -1, 1);
    this.localMoveLateral = THREE.MathUtils.clamp(worldDir.dot(side) * amount, -1, 1);
    // Mid-air: keep the input alive so air-control speed logic can lean the
    // player forward, but don't pivot the body (would snap-rotate the model
    // mid-jump which reads as a glitch).
    if (playerAirborne) {
      this.player.setDesiredFacing(null, 0);
      return;
    }
    this.player.setDesiredFacing(Math.atan2(worldDir.x, worldDir.z), amount);
  }

  /** Silent respawn for the new "just fall via gravity" flow. No fall
   *  state, no animation, no SFX — just teleport back to the last
   *  checkpoint after 2s of being in true free-fall. */
  private respawnFromFreefall(now: number) {
    this.unsupportedSince = null;
    // Survival arena (Level 10): falling off is fatal. Eliminate the
    // player — in multiplayer, drop into spectator on a remaining
    // runner; in solo, end the run as DNF. Last player alive wins.
    if (this.doubleSpinner) {
      this.runState.paused = false;
      // No crumble, no slow-mo — the player just falls cleanly into the
      // void (they've already been free-falling 2s to get here).
      sfx.fall();
      this.reportSurvivalFall();
      return;
    }
    let baseRespawn = this.lastCheckpointS >= 0 ? this.lastCheckpointS + 2 : 4;
    if (this.path.hitParade && baseRespawn < this.path.hitParade.flatStart) {
      baseRespawn = Math.max(baseRespawn, this.path.hitParade.flatStart * 0.5);
    }
    baseRespawn = Math.max(baseRespawn, 4);
    baseRespawn = THREE.MathUtils.clamp(baseRespawn, 0, Math.max(0, this.path.length - 8));
    // Clear any fall/lane state so the player lands cleanly back on the
    // ground instead of respawning mid-fall.
    this.unsupportedSince = null;
    this.mazeRushFallY = null;
    this.mazeRushFallVY = 0;
    this.mazeRushLaneIdx = -1;
    this.fallRespawnAtS = baseRespawn;
    this.runState.distance = baseRespawn;
    this.runState.invulnerableUntil = now + 1300;
    this.player.resetOnCourse(this.path, baseRespawn, 0);
    this.player.update(0, now);
    // Snap onto whatever solid surface is at the respawn point so the player
    // doesn't immediately re-enter freefall on a level with a raised floor.
    this.resolveStandableSurface();
    this.applyCamera(this.runState.distance, now);
    this.emitState();
  }

  private resolvePhysicalObstacleContacts(now: number): boolean {
    const playerS = this.runState.distance;
    const playerLaneOffset = this.player.laneOffset;
    const playerRadius = this.player.getRadius();
    const playerLateralVelocity = this.player.getLateralVelocity();
    // "Jumping" for obstacle pass-through means actually airborne — not just
    // elevated because of a standable surface like a platform. Without this
    // check, standing on a platform (y ≈ 0.55) would let you "clear" every
    // obstacle that's jump-passable (spinners, crates, etc.).
    const jumping = this.player.y > 0.5 && !this.player.isGroundedOnSurface();
    const sliding = false; // slide removed

    for (const o of this.obstacles) {
      if (o.resolved || !o.getContact) continue;
      const oz = o.getZ();
      if (oz < playerS - 8 || oz > playerS + 60) continue;
      if (o.passes({ jumping, sliding })) continue;
      const contact = o.getContact({
        playerS,
        playerLaneOffset,
        playerY: this.player.y,
        playerRadius,
        playerForwardVelocity: this.runState.speed,
        playerLateralVelocity,
      });
      if (!contact) continue;
      this.resolveContactPenetration(contact);
      if (!contact.isForceful) {
        this.runState.speed = Math.min(this.runState.speed, 0);
        return true;
      }
      this.applyPhysicalImpact(o, contact, now);
      return true;
    }
    return false;
  }

  private resolveContactPenetration(contact: ObstacleContact) {
    const correction = Math.min(contact.penetration + 0.03, 0.42);
    if (correction <= 0) return;
    this.runState.distance = THREE.MathUtils.clamp(
      this.runState.distance + contact.normalS * correction,
      0,
      this.path.length,
    );
    this.player.s = this.runState.distance;
    const lateral = this.player.laneOffset + contact.normalL * correction;
    this.player.laneOffset = THREE.MathUtils.clamp(lateral, -COURSE_FALL_HALF_WIDTH - 0.4, COURSE_FALL_HALF_WIDTH + 0.4);
  }

  private applyPhysicalImpact(o: ObstacleController, contact: ObstacleContact, now: number) {
    const alreadyDown = this.player.state === "hit";
    const canCountHit = !alreadyDown && now >= this.runState.invulnerableUntil;
    const grounded = this.player.y <= 0.12 && this.player.state !== "jump";
    const playerMass = alreadyDown ? 2.4 : grounded ? 5.2 : 3.1;
    const obstacleMass = o.applyPhysicsImpulse ? Math.max(1, o.mass ?? 3) : 200;
    const obstacleSpeed = Math.hypot(contact.obstacleVelocityS, contact.obstacleVelocityL);
    const relNormalSpeed = Math.max(0, contact.relativeSpeed);
    const transfer = obstacleMass / (obstacleMass + playerMass);
    const massScale = Math.sqrt(obstacleMass / Math.max(1, playerMass));
    const speedScale = THREE.MathUtils.clamp(relNormalSpeed / 6, 0.45, 1.85);
    const normalImpulse = (2.8 + relNormalSpeed * 0.82) * transfer * massScale;

    // Surface drag lets a rotating bar carry the player along its sweep, while
    // the main impulse still comes from the contact normal like a rigid-body hit.
    const tangentS = -contact.normalL;
    const tangentL = contact.normalS;
    const obstacleTangentSpeed = contact.obstacleVelocityS * tangentS + contact.obstacleVelocityL * tangentL;
    const dragScale = alreadyDown ? 0.72 : grounded ? 0.3 : 0.48;
    const tangentImpulse = obstacleTangentSpeed * dragScale * transfer;
    const groundedResistance = grounded && !alreadyDown ? 0.82 : 1.08;

    const lateralImpulse = (contact.normalL * normalImpulse + tangentL * tangentImpulse) * groundedResistance;
    const forwardImpulse = (contact.normalS * normalImpulse + tangentS * tangentImpulse) * groundedResistance;
    const verticalImpulse = o.kind === "boulder"
      ? THREE.MathUtils.clamp(0.38 + relNormalSpeed * 0.07 * massScale, 0.35, 1.5)
      : THREE.MathUtils.clamp((alreadyDown ? 0.04 : 0.1) + relNormalSpeed * 0.03 * massScale, 0.04, 0.52);

    this.player.knockdown(now, lateralImpulse, verticalImpulse);
    const nextPush = alreadyDown ? this.playerKnockbackSpeed + forwardImpulse : forwardImpulse;
    this.playerKnockbackSpeed = THREE.MathUtils.clamp(nextPush, -18, 18);
    this.knockSlowUntil = now + THREE.MathUtils.clamp(500 + relNormalSpeed * 95, 700, 1650);
    // Ball-on-player: trigger the bounce SFX so the impact reads sonically.
    if (o.kind === "bouncing-ball") {
      sfx.ballBounce(Math.min(1, 0.5 + relNormalSpeed * 0.08));
    }
    o.applyPhysicsImpulse?.(
      -forwardImpulse * playerMass * 0.16,
      -lateralImpulse * playerMass * 0.16,
    );
    o.impact?.({
      now,
      playerS: this.runState.distance,
      playerLaneOffset: this.player.laneOffset,
      playerForwardVelocity: this.runState.speed,
    });

    if (canCountHit) {
      this.runState.stumbles += 1;
      this.runState.score = Math.max(0, this.runState.score - 15);
      this.runState.invulnerableUntil = now + obstacleRecoveryMs(o) + this.extraInvulnerableMs;
      this.shake(
        now,
        THREE.MathUtils.clamp(260 + relNormalSpeed * 30, 280, 680),
        THREE.MathUtils.clamp(0.25 + speedScale * 0.28, 0.28, 0.9),
      );
      this.onEvent({ type: "hit" });
      this.emitState();
    }
  }

  private updateDynamicObstacleContacts() {
    const dynamic = this.obstacles.filter((o) => !o.resolved && o.applyPhysicsImpulse && o.getLateralOffset);
    for (let i = 0; i < dynamic.length; i++) {
      for (let j = i + 1; j < dynamic.length; j++) {
        const a = dynamic[i]!;
        const b = dynamic[j]!;
        const dz = b.getZ() - a.getZ();
        const dl = b.getLateralOffset!() - a.getLateralOffset!();
        const aR = a.getRadius?.() ?? 0.88;
        const bR = b.getRadius?.() ?? 0.88;
        const minSep = aR + bR;
        const minSepSq = minSep * minSep;
        const distSq = dz * dz + dl * dl;
        if (distSq > minSepSq || distSq < 0.0001) continue;
        const dist = Math.sqrt(distSq);
        const nz = dz / dist;
        const nl = dl / dist;
        const aMass = a.mass ?? 3;
        const bMass = b.mass ?? 3;
        // If both sides expose full velocity + radius-aware impulse, run a
        // proper elastic-collision-with-restitution exchange (ball ↔ ball).
        // Otherwise fall back to the legacy positional shove.
        const canElastic =
          a.getVelocityS && a.getVelocityL && a.applyVelocityImpulse &&
          b.getVelocityS && b.getVelocityL && b.applyVelocityImpulse;
        if (canElastic) {
          const avS = a.getVelocityS!();
          const avL = a.getVelocityL!();
          const avY = a.getVelocityY?.() ?? 0;
          const bvS = b.getVelocityS!();
          const bvL = b.getVelocityL!();
          const bvY = b.getVelocityY?.() ?? 0;
          // Relative velocity of B from A's frame.
          const relS = bvS - avS;
          const relL = bvL - avL;
          // Project onto contact normal (horizontal plane); normal y=0 here.
          const dot = relS * nz + relL * nl;
          if (dot < 0) {
            const restitution = 0.78;
            const k = -(1 + restitution) * dot / (1 / aMass + 1 / bMass);
            const iS = k * nz;
            const iL = k * nl;
            a.applyVelocityImpulse!(-iS / aMass, -iL / aMass, 0);
            b.applyVelocityImpulse!(iS / bMass, iL / bMass, 0);
            // Small vertical kick — collisions pop the balls up so they don't
            // grind along the ground after impact.
            const popA = Math.min(2.6, Math.abs(dot) * 0.18);
            const popB = Math.min(2.6, Math.abs(dot) * 0.18);
            a.applyVelocityImpulse!(0, 0, popA * 0.4);
            b.applyVelocityImpulse!(0, 0, popB * 0.4);
            // Also swap a fraction of vertical velocity so a falling ball can
            // bump a grounded one upward.
            const relY = bvY - avY;
            if (relY < -1) {
              a.applyVelocityImpulse!(0, 0, -relY * 0.25);
              b.applyVelocityImpulse!(0, 0, relY * 0.25);
            }
            // SFX (rate-limited inside obstacles module).
            const dist0 = Math.abs((a.getZ() + b.getZ()) / 2 - this.runState.distance);
            if (dist0 < 22) {
              const prox = 1 - dist0 / 22;
              sfx.ballBounce(Math.min(0.9, prox * (0.4 + Math.abs(dot) * 0.06)));
            }
          }
          // Also resolve penetration so they don't sit overlapping.
          const pen = minSep - dist;
          const halfPen = pen * 0.5;
          a.applyPhysicsImpulse!(-nz * halfPen * 6 * aMass, -nl * halfPen * 6 * aMass);
          b.applyPhysicsImpulse!(nz * halfPen * 6 * bMass, nl * halfPen * 6 * bMass);
        } else {
          const strength = (minSepSq - distSq) * 2.2;
          a.applyPhysicsImpulse!(-nz * strength * bMass, -nl * strength * bMass);
          b.applyPhysicsImpulse!(nz * strength * aMass, nl * strength * aMass);
        }
      }
    }
  }

  private rebuildSupportMeshes() {
    const meshes: THREE.Object3D[] = [];
    const skip = new Set<THREE.Object3D>([this.player.group, this.countdownGroup].filter(Boolean) as THREE.Object3D[]);
    const isSkipped = (obj: THREE.Object3D) => {
      for (const root of skip) {
        let current: THREE.Object3D | null = obj;
        while (current) {
          if (current === root) return true;
          current = current.parent;
        }
      }
      return false;
    };
    this.scene.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (isSkipped(obj)) return;
      // Author-tagged decor (e.g. survival arena's rim torus). These
      // meshes are visible but must never act as a standable / blocking
      // surface — players were climbing the rim and getting stuck.
      if (obj.userData?.decorOnly) return;
      const mat = obj.material;
      const transparent = Array.isArray(mat) ? mat.some((m) => m.transparent && m.opacity < 0.75) : mat.transparent && mat.opacity < 0.75;
      if (transparent) return;
      meshes.push(obj);
    });
    this.supportMeshes = meshes;
  }

  /**
   * Per-frame moving-platform handling. Multi-platform safe: iterates every
   * platform, sticks the player to the one they're standing on, and applies
   * side-push physics when they're in a platform's footprint but not on top.
   *
   * Future levels can simply push more platforms onto `this.platforms` and
   * this loop will handle the rest.
   */
  private updatePlatforms(dt: number, now: number) {
    if (this.platforms.length === 0) return;
    const playerLat = this.player.laneOffset + this.player.slotOffset;
    const playerS = this.runState.distance;
    const playerY = this.player.y;
    const playerJumping = this.player.state === "jump";
    const playerHit = this.player.state === "hit";

    // Tick all platforms first so positions are up-to-date.
    const states = this.platforms.map((p, i) => {
      const s = p.tick(dt);
      this.platformStates[i] = s;
      return s;
    });

    // If the player just got hit by a ball / spinner / other dynamic
    // obstacle, release the platform so the knockback impulse actually
    // throws them off. Otherwise the rider-lock would snap them back to
    // the platform position every frame and the hit would feel inert.
    if (playerHit) {
      if (this.ridingPlatform >= 0) {
        const prev = states[this.ridingPlatform];
        if (prev) this.releasePlatform(prev);
      }
      // Skip ride/snap logic this frame — let the knockback fly free.
      // Side-push physics are also skipped (platform won't shove an
      // already-knockdown player further). Resume normal behavior once
      // the hit phase ends.
      return;
    }

    // Carry momentum from the platform into the air for ~400ms after jumping
    // off — feels natural and lets the player launch off a moving platform.
    if (now < this.platformLaunchUntil && playerJumping) {
      const dec = Math.exp(-2.8 * dt);
      this.platformLaunchVel *= dec;
      // If the player is actively pushing forward, don't let a boat
      // moving backward at jump-time cancel their jump arc. The
      // backward platform momentum just gets discarded — the launch
      // only carries momentum that aligns with the player's intent
      // (or that they aren't actively fighting).
      const intent = this.localMoveForward;
      const aligned = intent > 0.25
        ? Math.max(0, this.platformLaunchVel)
        : intent < -0.25
          ? Math.min(0, this.platformLaunchVel)
          : this.platformLaunchVel;
      this.runState.distance += aligned * dt;
    }

    // Pick the best candidate to ride. We only "attach" to a platform when
    // the player is actually landing on it — i.e. coming down (vy ≤ 0) and
    // near the top surface. Jumping UP through the air must NOT trigger an
    // attach, otherwise the jump animation gets cut short.
    let bestRide = -1;
    let bestRideDist = Infinity;
    const topY = 0.55; // path-local height of platform top
    const isAscending = this.player.vy > 0.05;
    for (let i = 0; i < states.length; i++) {
      const st = states[i]!;
      const dS = playerS - st.s;
      const dL = playerLat - (st.centerL ?? 0);
      if (Math.abs(dS) > st.halfDepth + 0.4) continue;
      if (Math.abs(dL) > st.halfWidth + 0.4) continue;
      const heightDelta = playerY - topY;
      // Only "attach" if at platform height AND not ascending. While jumping
      // up the player simply passes through — no lock, no animation cut.
      if (this.ridingPlatform !== i) {
        if (isAscending) continue;
        if (playerJumping) continue;
        // Must be near the top surface (just landed). Allow a small slop
        // above (player feet hit the top) but not way above.
        if (heightDelta < -0.18 || heightDelta > 0.35) continue;
      } else {
        // Already riding — keep until player jumps off or walks off edge.
        if (playerJumping) continue;
        if (heightDelta < -0.4 || heightDelta > 0.8) continue;
      }
      const score = Math.hypot(dS, dL) + Math.abs(heightDelta);
      if (score < bestRideDist) {
        bestRideDist = score;
        bestRide = i;
      }
    }

    if (bestRide >= 0) {
      const st = states[bestRide]!;
      if (this.ridingPlatform !== bestRide) {
        // Just landed on this platform — capture offsets.
        this.platformOffsetS = THREE.MathUtils.clamp(playerS - st.s, -st.halfDepth, st.halfDepth);
        this.platformOffsetL = THREE.MathUtils.clamp(playerLat - (st.centerL ?? 0), -st.halfWidth, st.halfWidth);
        this.ridingPlatform = bestRide;
      }
      // Player can run freely along the platform top with normal running speed.
      // Forward/back AND lateral motion both contribute, projected onto the
      // platform's local frame (which equals path frame here).
      const runSpeed = (this.config.runSpeed ?? RUNNER.baseSpeed) * 0.85;
      const lateralSpeed = 9;
      this.platformOffsetS += this.localMoveForward * runSpeed * dt;
      this.platformOffsetL += this.localMoveLateral * lateralSpeed * dt;
      // River-boat: the boat rails are solid. The player can run within
      // the boat freely but the gunwales clamp lateral motion — they
      // can't walk off the side, only jump. Forward/back overflow
      // (off the bow/stern) still releases so they can step onto a
      // dock at the ends.
      const railBlocked = !!this.path.riverBoat;
      if (railBlocked) {
        this.platformOffsetL = THREE.MathUtils.clamp(
          this.platformOffsetL,
          -st.halfWidth + 0.1,
          st.halfWidth - 0.1,
        );
      }
      // If they've walked off either edge, release the platform.
      const lateralOff = !railBlocked && Math.abs(this.platformOffsetL) > st.halfWidth + 0.05;
      if (
        Math.abs(this.platformOffsetS) > st.halfDepth + 0.05 ||
        lateralOff
      ) {
        this.releasePlatform(st);
      } else {
        this.runState.distance = THREE.MathUtils.clamp(
          st.s + this.platformOffsetS,
          0,
          this.path.length,
        );
        // Sync lateral lane offset to the platform-local offset so the player
        // visually stands on the platform without drift.
        const targetLat = (st.centerL ?? 0) + this.platformOffsetL - this.player.slotOffset;
        // Smooth lateral correction to avoid snap when joystick is centered.
        const followL = 1 - Math.exp(-18 * dt);
        this.player.laneOffset += (targetLat - this.player.laneOffset) * followL;
        // Stand on the platform top so vertical animation reads correctly.
        if (this.player.vy <= 0.05) this.player.landOn(0.55);
      }
    } else {
      // Not on any platform. If we just stepped off (walked off edge or
      // jumped), remember the platform's velocity so the jump carries
      // momentum. Critical for jumping off cleanly without glitching.
      if (this.ridingPlatform >= 0) {
        const prev = states[this.ridingPlatform];
        if (prev) this.releasePlatform(prev);
      }
    }
    // Hard release whenever the player enters the jump state. Without this,
    // pressing jump while atop the platform would keep the rider-lock active
    // for one extra frame and the launch felt sticky.
    if (playerJumping && this.ridingPlatform >= 0) {
      const prev = states[this.ridingPlatform];
      if (prev) this.releasePlatform(prev);
    }

    // Side / front / back collision. Real AABB-vs-capsule: find the closest
    // exit axis and push the player out by exactly the penetration (so the
    // capsule edge sits cleanly against the platform face, no clipping).
    // We don't apply this while the player is already riding a platform, so
    // mid-platform standing doesn't fight the rider-lock.
    // No side-push while the player is mid-jump — air-steering must stay
    // smooth so the player can aim straight from a dock onto a passing
    // boat. The rider-attach loop above takes over once they're at deck
    // height. The hull only blocks them when they're on the water level.
    if (!playerJumping && this.player.y < 0.5 && this.ridingPlatform < 0) {
      const playerRadius = this.player.getRadius();
      for (let i = 0; i < states.length; i++) {
        const st = states[i]!;
        // Sanity: if the player somehow ended up above the deck without
        // attaching (e.g. landed on the very edge), still let them drop.
        if (this.player.y > st.topY - 0.15) continue;
        const dS = playerS - st.s;
        const dL = playerLat - (st.centerL ?? 0);
        // Penetration depths along each axis (positive = how far inside the
        // AABB the player capsule edge is).
        const penS = (st.halfDepth + playerRadius) - Math.abs(dS);
        const penL = (st.halfWidth + playerRadius) - Math.abs(dL);
        if (penS <= 0 || penL <= 0) continue; // outside the inflated AABB
        // Closest exit axis: the smaller positive penetration.
        if (penS < penL) {
          // Exit along path-s. Direction = sign(dS), but if dead-centered use
          // the platform's motion direction (it's coming at us from that side).
          const dir = dS !== 0 ? Math.sign(dS) : st.velocityS >= 0 ? 1 : -1;
          const targetS = st.s + dir * (st.halfDepth + playerRadius + 0.02);
          // Smooth follow — stronger if platform is closing on us fast.
          const closing = Math.max(0.5, Math.abs(st.velocityS));
          const k = 1 - Math.exp(-(14 + closing * 1.2) * dt);
          this.runState.distance = THREE.MathUtils.clamp(
            this.runState.distance + (targetS - this.runState.distance) * k,
            0,
            this.path.length,
          );
          // Carry platform's path-s velocity into the player so they're
          // physically pushed along, not just teleported.
          if (Math.sign(st.velocityS) === dir) {
            this.runState.distance += st.velocityS * dt * 0.5;
          }
        } else {
          // Exit along lateral axis. Push laneOffset out and use a quick
          // impulse so the player slides cleanly off the side.
          const dir = dL !== 0 ? Math.sign(dL) : 1;
          const targetL = (st.centerL ?? 0) + dir * (st.halfWidth + playerRadius + 0.02);
          const k = 1 - Math.exp(-16 * dt);
          this.player.laneOffset += (targetL - this.player.laneOffset) * k;
          if (Math.sign(st.velocityS) === 0) {
            this.player.applyImpulse(dir * 1.4, 0);
          }
        }
        break;
      }
    }
  }

  /** Player has just stepped off (or jumped off) a platform — store its
   *  velocity as a brief launch impulse so the jump carries momentum. */
  private releasePlatform(st: { velocityS: number }) {
    this.platformLaunchVel = st.velocityS;
    this.platformLaunchUntil = performance.now() + 450;
    this.ridingPlatform = -1;
  }

  /** Assist-mode auto-jump. Triggers a jump when the player is ~2-3m from
   *  any obstacle in their current lateral band that doesn't allow walk-
   *  through. Cheap heuristic — close enough for a kid to clear most things. */
  private maybeAutoJump(now: number) {
    if (this.player.state !== "run") return;
    if (this.player.y > 0.18) return; // already airborne
    if (now - this.lastAutoJumpAt < 320) return;
    const s = this.runState.distance;
    const lateral = this.player.laneOffset + this.player.slotOffset;
    for (const o of this.obstacles) {
      if (o.resolved) continue;
      const oz = o.getZ();
      const ahead = oz - s;
      if (ahead < 1.4 || ahead > 3.4) continue;
      // Only consider obstacles whose footprint actually intersects our band.
      const ol = o.getLateralOffset?.() ?? 0;
      if (Math.abs(ol - lateral) > 1.8) continue;
      // Spinner-arm sweep cuts wider — give a bit more slack laterally.
      if (o.kind === "spinner" || o.kind === "ice-spinner") {
        // Always jump near spinners.
      } else if (!o.passes({ jumping: true, sliding: false })) {
        // Unjumpable obstacle (e.g. boulder) — don't bother.
        continue;
      }
      this.player.jump(now);
      sfx.jump();
      this.lastAutoJumpAt = now;
      return;
    }
    // Also auto-jump approaching blocked doors so the kid sails through.
    if (this.doorDash) {
      for (const d of this.doorDash.doors) {
        const ahead = d.s - s;
        if (ahead < 1.2 || ahead > 3.0) continue;
        if (Math.abs(d.l - lateral) > 1.4) continue;
        this.player.jump(now);
        sfx.jump();
        this.lastAutoJumpAt = now;
        return;
      }
    }
  }

  /** Apply a "bar contact" with natural physics:
   *   • ALWAYS push the player out of penetration so they can't clip through.
   *   • Zero forward speed if the bar pushed them back along s.
   *   • Only fire a knockdown + impulse + sfx when the bar's velocity into
   *     the player along the contact normal exceeds a threshold. Running
   *     into a stationary or slow-moving blade just gets blocked; a fast-
   *     swinging blade still flings them.
   *  Used by every swinging-bar obstacle (circle-spinner, maze-rush
   *  pendulum, rolling-tubes axe). */
  private applyBarImpact(
    now: number,
    hit: { normalS?: number; normalL: number; penetration: number; barVelS?: number; barVelL?: number },
    opts: { knockbackThreshold?: number; impulseScale?: number; shakeAmp?: number } = {},
  ) {
    const normalS = hit.normalS ?? 0;
    const KNOCKBACK_THRESHOLD = opts.knockbackThreshold ?? 4;
    const IMPULSE_SCALE = opts.impulseScale ?? 1.3;
    const SHAKE = opts.shakeAmp ?? 0.32;

    // 1. Push out of penetration (block).
    this.runState.distance = THREE.MathUtils.clamp(
      this.runState.distance + normalS * (hit.penetration + 0.04),
      0,
      this.path.length,
    );
    this.player.s = this.runState.distance;
    this.player.laneOffset = THREE.MathUtils.clamp(
      this.player.laneOffset + hit.normalL * (hit.penetration + 0.04),
      -8,
      8,
    );
    if (normalS < -0.25) {
      this.runState.speed = Math.min(this.runState.speed, 0);
    }

    // 2. Compute the bar's velocity INTO the player along the contact normal.
    const barVelS = hit.barVelS ?? 0;
    const barVelL = hit.barVelL ?? 0;
    const playerVelL = this.player.getLateralVelocity?.() ?? 0;
    const playerVelS = this.runState.speed;
    const relVelS = barVelS - playerVelS;
    const relVelL = barVelL - playerVelL;
    const impactSpeed = Math.max(0, relVelL * hit.normalL + relVelS * normalS);

    // Fall Guys-style impact tiers:
    //   • Light  — just a shove. Keep running, keep jumping.
    //   • Big    — full ragdoll: the player tumbles in the direction of
    //              the hit, lifts off briefly, then stands back up.
    // The big threshold is high enough that only a fast-swinging bar
    // or a real cannonball hit triggers it; brushing past a slow bar
    // just nudges you.
    const BIG_IMPACT_THRESHOLD = KNOCKBACK_THRESHOLD * 2.4;
    if (impactSpeed > KNOCKBACK_THRESHOLD && now >= this.runState.invulnerableUntil) {
      const punch = impactSpeed * IMPULSE_SCALE;
      this.player.applyImpulse(hit.normalL * punch, 0);
      this.playerKnockbackSpeed = normalS * punch;
      this.runState.invulnerableUntil = now + 350;
      this.runState.stumbles += 1;
      this.shake(now, 240, SHAKE);
      sfx.hit();
      this.onEvent({ type: "hit" });
      if (impactSpeed > BIG_IMPACT_THRESHOLD) {
        // Big hit → ragdoll. Direction matches the bar's normal so the
        // player visibly flies the way they were hit. Stronger vertical
        // pop than the default so the tumble feels weighty.
        this.player.knockdown(now, hit.normalL * punch * 1.1, 0.9);
      }
    }
  }

  /** Circle-spinner: each circular platform has a long bar sweeping around
   *  it. Natural-physics: always blocks, only knocks down on real impact
   *  speed (relative velocity along the contact normal). The hit-state
   *  early-return was removed so the bar keeps pushing the player even if
   *  they're already tumbling — the bar can no longer pass through them. */
  /** Train-roof: swinging signs + tunnel crates are LOW bars — knock the
   *  player sideways unless they've jumped clear. */
  private handleTrainRoofCollision(now: number) {
    if (!this.trainRoof) return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const playerR = this.player.getRadius();
    const hit = this.trainRoof.barContact(s, l, playerR);
    if (!hit) return;
    // Jumped clear: skip while AIRBORNE rather than on an absolute height —
    // the lanes bob up/down, so player.y while grounded isn't a fixed value.
    if (!this.player.isGroundedOnSurface()) return;
    this.applyBarImpact(now, hit, { knockbackThreshold: 1.6, impulseScale: 1.5, shakeAmp: 0.3 });
    this.emitState();
  }

  private handleCircleSpinnerCollision(now: number) {
    if (!this.circleSpinner) return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const playerR = this.player.getRadius();
    const hit = this.circleSpinner.barContact(s, l, playerR);
    if (!hit) return;
    // Lower bar: jumpable. Upper bar: must NOT be airborne (stay on
    // the deck to duck under). Each platform now has both bars.
    if (!hit.isUpper && this.player.y > 1.05) return; // jumped clear
    if (hit.isUpper && this.player.y < 0.1) return;   // ducked / on the deck
    this.applyBarImpact(now, hit);
    this.emitState();
  }

  /** Double-spinner survival: two bars + off-edge elimination.
   *
   *   • Lower bar is jumpable (skip hit while player.y > 1.05).
   *   • Upper bar requires ducking (skip hit while player.y < 0.1).
   *   • Bar hits get a beefed-up impulse so they actually push the player
   *     toward the edge — survival should feel like a real shove.
   *   • If the player ends up beyond `platformRadius + grace` from the
   *     arena centre, we trigger the existing DNF path (`finishRun(false)`)
   *     which plays the crumble animation and ends the run for that
   *     player. In multiplayer the server treats DNF players as scored
   *     against last-survivor, so the lobby naturally collapses to a
   *     single winner.
   */
  private handleDoubleSpinnerCollision(now: number) {
    if (!this.doubleSpinner) return;
    let s = this.runState.distance;
    let l = this.player.laneOffset + this.player.slotOffset;
    const playerR = this.player.getRadius();

    // ── Pillar collision — push the player out of the central pole so
    //    they can't walk into / through it. Applied before bar contact
    //    so a bar hitting them into the pillar still results in a clean
    //    block instead of intersecting geometry.
    {
      const pc = this.doubleSpinner.pillarContact(s, l, playerR);
      if (pc) {
        this.runState.distance = THREE.MathUtils.clamp(
          this.runState.distance + pc.normalS * (pc.penetration + 0.02),
          0,
          this.path.length,
        );
        this.player.s = this.runState.distance;
        this.player.laneOffset = THREE.MathUtils.clamp(
          this.player.laneOffset + pc.normalL * (pc.penetration + 0.02),
          -12,
          12,
        );
        // Bleed any inward velocity so the player doesn't keep grinding
        // against the pillar every frame.
        if (this.runState.speed > 0 && pc.normalS < 0) {
          this.runState.speed = Math.max(0, this.runState.speed * 0.4);
        }
        s = this.runState.distance;
        l = this.player.laneOffset + this.player.slotOffset;
      }
    }

    // ── Bar contact ─────────────────────────────────────────────────────
    const hit = this.doubleSpinner.barContact(s, l, playerR);
    if (hit) {
      const skipLower = hit.bar === "lower" && this.player.y > 1.05;
      const skipUpper = hit.bar === "upper" && this.player.y < 0.1;
      if (!skipLower && !skipUpper) {
        // Heavier impulse than circle-spinner — survival needs real shove.
        this.applyBarImpact(now, hit, {
          knockbackThreshold: 1.5,
          impulseScale: hit.bar === "upper" ? 1.9 : 1.55,
          shakeAmp: 0.30,
        });
        this.emitState();
      }
    }

    // Survival-arena elimination is now driven SOLELY by the global
    // 2-second freefall watchdog (respawnFromFreefall in update()).
    // The previous off-edge / over-gap checks here used to fire
    // instantly when ANY tile dropped — sometimes catching a player
    // who wasn't actually standing on it (raycast jitter during the
    // shake animation, plus the angular-snap edge case). Removing
    // them means the only way to die is to actually be in free fall
    // for 2 s, exactly as the player expects.
    //
    // ── Invisible rim wall ──────────────────────────────────────────────
    // Players must NOT be able to run or jump off the outer edge — the only
    // legitimate way to fall is straight DOWN through a tile that has dropped
    // away. So clamp the player's radial distance from the arena centre to
    // just inside the rim. This is purely horizontal: vertical position (and
    // therefore falling through a gap) is untouched, and the clamp runs even
    // while airborne so a jump toward the edge bonks the wall instead of
    // sailing off.
    {
      const c = this.doubleSpinner.arenaCenter;
      const maxR = this.doubleSpinner.platformRadius - playerR - 0.1;
      const ds = s - c.s;
      const dl = l - c.l;
      const dist = Math.hypot(ds, dl);
      if (dist > maxR && dist > 1e-4) {
        const k = maxR / dist;
        this.runState.distance = THREE.MathUtils.clamp(c.s + ds * k, 0, this.path.length);
        this.player.s = this.runState.distance;
        this.player.laneOffset = THREE.MathUtils.clamp((c.l + dl * k) - this.player.slotOffset, -12, 12);
        // Bleed outward momentum so the player slides along the wall instead
        // of grinding into it every frame.
        if (this.runState.speed > 0) this.runState.speed *= 0.5;
        this.jumpCarrySpeed = 0;
      }
    }
  }

  /** Rolling-Tubes spinner-bar collision. Player must jump over the
   *  bar near the foot of each tube — clipping it gets a light shove
   *  (same shape as the survival arena's lower bar). */
  private handleRollingTubeSpinnerCollision(now: number) {
    if (!this.rollingTubes) return;
    if (this.player.y > 1.05) return; // jumped clear
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const playerR = this.player.getRadius();
    const hit = this.rollingTubes.barContact(s, l, playerR);
    if (!hit) return;
    this.applyBarImpact(now, hit, { knockbackThreshold: 2.5, impulseScale: 1.3, shakeAmp: 0.24 });
    this.emitState();
  }

  /** Rolling-tubes (Level 3 pyramid) ball-cannon collision. The tilted
   *  cannon at the top fires bouncing balls down the steps; this checks
   *  whether any of them is overlapping the player capsule. If so,
   *  knock the player back and consume the ball so a single hit doesn't
   *  re-trigger across many frames. Jumping clears the ball normally
   *  via the 3D-vs-capsule check inside ballContact. */
  private handleRollingTubeBallCollision(now: number) {
    if (!this.rollingTubes) return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const worldY = this.player.group.position.y;
    const playerR = this.player.getRadius();
    const hit = this.rollingTubes.ballContact(s, l, worldY, playerR);
    if (!hit) return;
    this.applyBarImpact(now, hit, {
      knockbackThreshold: 0.4,
      impulseScale: 2.2,
      shakeAmp: 0.35,
    });
    hit.consume();
    this.emitState();
  }

  /** Block-Climb (Level 9) ball-cannon collision. Each tick the decor
   *  module ages the live balls; we just need to ask whether any of them
   *  are overlapping the player. If so, knock them down and spend the
   *  ball so a single hit doesn't slam them with the same projectile
   *  many frames in a row. Jumping over a rolling ball lets it pass. */
  private handleBlockClimbBallCollision(now: number) {
    if (!this.blockClimb) return;
    if (this.player.y > 0.7) return; // airborne — jumped clean
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const playerR = this.player.getRadius();
    const hit = this.blockClimb.ballContact(s, l, playerR);
    if (!hit) return;
    this.applyBarImpact(now, hit, {
      // Stronger shove than before — per playtest the cannon balls
      // barely budged the player. Bigger impulse + lower threshold so
      // even a light brush knocks them backward off the climb edge.
      knockbackThreshold: 0.2,
      impulseScale: 2.6,
      shakeAmp: 0.4,
    });
    hit.consume();
    this.emitState();
  }

  /** Maze-Rush per-frame lane resolution. Players commit to a lane while
   *  inside a section; if they drift off the lane laterally they don't get
   *  hard-clamped — instead they enter a "between-lanes" descent. World Y
   *  drops under gravity; if it crosses another lane (lateral matches AND
   *  Y passes through), they land on that lane. Drop too far and they fall
   *  off the world entirely (respawned by the freefall watchdog). */
  private updateMazeRushLane(dt: number, now: number) {
    const mr = this.path.mazeRush;
    if (!mr) return;
    const s = this.runState.distance;
    const secIdx = this.path.mazeRushSectionIdx(s);
    const lateral = this.player.laneOffset + this.player.slotOffset;
    const half = this.path.mazeRushLaneHalfWidth();

    if (secIdx < 0) {
      // On a platform — release any lane commit and any between-lane fall.
      this.mazeRushLaneIdx = -1;
      this.mazeRushFallY = null;
      this.mazeRushFallVY = 0;
      return;
    }

    // ── Between-lane fall in progress?
    if (this.mazeRushFallY != null) {
      const gravity = 18;
      const prevY = this.mazeRushFallY;
      this.mazeRushFallVY -= gravity * dt;
      this.mazeRushFallY += this.mazeRushFallVY * dt;
      // Check if we've descended through another lane.
      for (let i = 0; i < mr.laneCount; i++) {
        const w = this.path.mazeRushLaneWarpAt(i, s);
        if (Math.abs(lateral - w.dx) > half) continue;
        // Falling THROUGH the lane (was above, now at or below).
        if (prevY > w.dy && this.mazeRushFallY <= w.dy + 0.05) {
          // Land on it.
          this.mazeRushLaneIdx = i;
          this.mazeRushFallY = null;
          this.mazeRushFallVY = 0;
          this.player.y = 0;
          this.player.vy = 0;
          return;
        }
      }
      // Fell below every lane with nothing caught — respawn now. The global
      // freefall watchdog can't see this fall because the lane-fall keeps
      // player.vy ≈ 0, so we trigger it explicitly here.
      if (this.mazeRushFallY < -6) {
        this.respawnFromFreefall(now);
      }
      return;
    }

    // ── Lane commit if we don't have one yet.
    if (this.mazeRushLaneIdx < 0) {
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < mr.laneCount; i++) {
        const w = this.path.mazeRushLaneWarpAt(i, s);
        const d = Math.abs(lateral - w.dx);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      this.mazeRushLaneIdx = bestIdx;
    }

    // ── Jump UP onto a higher lane that the player can reach.
    // If the player has jumped high enough that their world-Y is at or
    // above another lane's centre AND they're vertically descending into
    // it AND lateral is inside that lane's bounds, commit to that lane.
    // Reads as "I jumped onto the lane above me."
    const warp = this.path.mazeRushLaneWarpAt(this.mazeRushLaneIdx, s);
    if (this.player.y > 0.05 && this.player.vy <= 0.05) {
      const playerWorldY = warp.dy + this.player.y;
      let bestHigherIdx = -1;
      let bestHigherDy = warp.dy + 0.001;
      for (let i = 0; i < mr.laneCount; i++) {
        if (i === this.mazeRushLaneIdx) continue;
        const w = this.path.mazeRushLaneWarpAt(i, s);
        if (Math.abs(lateral - w.dx) > half) continue;
        // Lane has to be ABOVE the current lane, and the player has to
        // have reached or passed its dy on the way down.
        if (w.dy > warp.dy + 0.2 && playerWorldY >= w.dy - 0.05) {
          if (w.dy > bestHigherDy) {
            bestHigherDy = w.dy;
            bestHigherIdx = i;
          }
        }
      }
      if (bestHigherIdx >= 0) {
        this.mazeRushLaneIdx = bestHigherIdx;
        this.player.y = 0;
        this.player.vy = 0;
        return;
      }
    }

    // ── If we drifted off our committed lane laterally, start a between-
    // lane descent from the lane's current Y. Lateral momentum is preserved
    // so the player keeps drifting and can land on a neighbour below.
    if (Math.abs(lateral - warp.dx) > half) {
      this.mazeRushFallY = warp.dy + this.player.y;
      this.mazeRushFallVY = Math.min(0, this.player.vy);
      this.mazeRushLaneIdx = -1;
      this.player.y = 0;
    }
  }

  /** Apply the lane's vertical lift (or the in-air between-lane Y) to the
   *  player mesh after physics. Render-only; physics stays flat. */
  private applyMazeRushRenderWarp() {
    const mr = this.path.mazeRush;
    if (!mr) return;
    if (this.dying) return;
    if (this.mazeRushFallY != null) {
      this.player.group.position.y += this.mazeRushFallY;
      return;
    }
    if (this.mazeRushLaneIdx < 0) return;
    const s = this.runState.distance;
    const warp = this.path.mazeRushLaneWarpAt(this.mazeRushLaneIdx, s);
    this.player.group.position.y += warp.dy;
  }

  /** Supermarket slip push — while standing inside a spill zone, push
   *  the player laterally in the zone's direction every frame. The push
   *  is on top of (and overrides braking on) the player's own steering,
   *  so even fighting against it they slide somewhat. */
  private applySupermarketSlipPush(dt: number) {
    if (!this.supermarketSweep) return;
    if (this.player.state === "hit") return;
    if (this.player.y > 0.2) return; // airborne — no slip
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const push = this.supermarketSweep.slipPushAt(s, l);
    if (push.dx === 0) return;
    // Direct laneOffset translation; mirrors the rolling-tubes treadmill.
    this.player.laneOffset += push.dx * dt;
    this.player.applyImpulse(push.dx * dt * 0.4, 0);
  }

  /** Supermarket falling-product contact — same natural-physics shape as
   *  the other bar handlers. Products falling from shelves knock the
   *  player back when they hit. */
  private handleSupermarketProductCollision(now: number) {
    if (!this.supermarketSweep) return;
    if (this.player.state === "hit") return;
    if (now < this.runState.invulnerableUntil) return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const worldY = this.player.group.position.y;
    const hit = this.supermarketSweep.productContact(s, l, worldY, this.player.getRadius());
    if (!hit) return;
    this.applyBarImpact(now, hit, { knockbackThreshold: 2.5, impulseScale: 1.2, shakeAmp: 0.22 });
    this.emitState();
  }

  /** River-Boat jumping-fish hit — uses the same natural-physics helper
   *  as the other bar-style contacts. Fish only register while above
   *  water (decor's fishContact filters that). */
  private handleRiverBoatFishCollision(now: number) {
    if (!this.riverBoat) return;
    if (this.player.state === "hit") return;
    if (now < this.runState.invulnerableUntil) return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const worldY = this.player.group.position.y;
    // Shark bite — drops the player into a slow-mo crumble + 1.4s
    // delay before respawning at the last checkpoint. Reuses the
    // existing `dying` state so the world keeps animating + camera
    // orbits while the body falls apart.
    if (!this.sharkDeathAt && this.riverBoat.sharkBite(s, l, worldY, this.player.getRadius())) {
      this.sharkDeathAt = now;
      this.dying = true;
      this.deathStartedAt = now;
      this.player.crumble();
      sfx.hit();
      return;
    }
    if (this.sharkDeathAt && now - this.sharkDeathAt > 1400) {
      this.sharkDeathAt = null;
      this.dying = false;
      this.timeScale = 1;
      this.respawnFromFreefall(now);
      return;
    }
    if (this.sharkDeathAt) return; // freeze normal collision while dying
    const hit = this.riverBoat.fishContact(s, l, worldY, this.player.getRadius());
    if (!hit) return;
    this.applyBarImpact(now, hit, { knockbackThreshold: 2, impulseScale: 1.4, shakeAmp: 0.28 });
    this.emitState();
  }

  /** Space-Ship spinner contact — natural-physics + continuous push. */
  private handleSpaceShipSpinnerCollision(now: number) {
    if (!this.spaceShip) return;
    // Was `player.y > 0.95` (jumped clear). Replaced with a level-relative
    // check so the lower rescue path's spinners can also be jumped over —
    // upper deck: jump >0.95 above deck top; lower path: jump >0.45 above
    // lower deck (player.y < -0.2 means we're on the lower track).
    const onLower = this.player.y < -0.2;
    if (!onLower && this.player.y > 0.95) return;
    if (onLower && this.player.y > -1.0) return; // jumping on lower path
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const hit = this.spaceShip.barContact(s, l, this.player.getRadius(), this.player.y);
    if (!hit) return;
    this.applyBarImpact(now, hit);
    this.emitState();
  }

  /** Maze-Rush swing-log contact — natural-physics. */
  private handleMazeRushPendulumCollision(now: number) {
    if (!this.mazeRush) return;
    const s = this.runState.distance;
    const lateral = this.player.laneOffset + this.player.slotOffset;
    const TRACK_TOP_Y = 0.18;
    let worldY: number;
    if (this.mazeRushFallY != null) {
      worldY = TRACK_TOP_Y + this.mazeRushFallY + this.player.y;
    } else if (this.mazeRushLaneIdx >= 0) {
      const w = this.path.mazeRushLaneWarpAt(this.mazeRushLaneIdx, s);
      worldY = TRACK_TOP_Y + w.dy + this.player.y;
    } else {
      worldY = TRACK_TOP_Y + this.player.y;
    }
    const playerR = this.player.getRadius();
    const hit = this.mazeRush.pendulumContact(s, lateral, worldY, playerR);
    if (!hit) return;
    // Maze-rush pendulums swing fast → keep them slightly more violent than
    // the default. The natural-physics gate still blocks low-impact brushes.
    this.applyBarImpact(now, hit, { knockbackThreshold: 4.5, impulseScale: 1.6, shakeAmp: 0.36 });
    this.emitState();
  }

  /** Rolling-tubes swinging-axe contact — natural-physics. */
  private handleRollingTubeAxeCollision(now: number) {
    if (!this.rollingTubes) return;
    const s = this.runState.distance;
    const lateral = this.player.laneOffset + this.player.slotOffset;
    const worldY = this.player.group.position.y;
    const hit = this.rollingTubes.axeContact(s, lateral, worldY, this.player.getRadius());
    if (!hit) return;
    // Big tube axes — high impulse when they DO hit at speed, but if the
    // player just brushed the resting blade they're only blocked.
    this.applyBarImpact(now, hit, { knockbackThreshold: 4.5, impulseScale: 1.55, shakeAmp: 0.34 });
    this.emitState();
  }

  /** Rolling-tubes: while the player is on a tube, the rotating surface
   *  carries them — like standing on a moving treadmill. We translate the
   *  player's lateral position directly by the surface's tangential
   *  velocity per frame, so they MUST steer against the spin to stay
   *  centered. Standing still = sliding off the side. */
  private applyRollingTubePush(dt: number) {
    if (!this.rollingTubes) return;
    if (this.player.state === "hit") return;
    if (this.player.y > 0.2 && !this.player.isGroundedOnSurface()) return;
    const tube = this.rollingTubes.tubeAt(this.runState.distance);
    if (!tube) return;
    // Tangential surface speed at the top of the cylinder (m/s).
    const surfaceVel = tube.angularVel * tube.radius;
    // Carry-mode: shift the player's actual lane offset by the surface
    // distance moved this frame, not just an impulse to their velocity.
    // Counter-steering with the joystick still works because the next-frame
    // lateral input adds to laneVel and offsets the carry.
    this.player.laneOffset += surfaceVel * dt;
    // Also nudge laneVel a little so the player's momentum reads "I'm moving"
    // rather than "I'm being teleported".
    this.player.applyImpulse(surfaceVel * dt * 0.3, 0);
  }

  /** Door-dash collision. Every door looks identical from the player's POV —
   *  the open/blocked state is hidden until they hit it. On first contact the
   *  decor animates the reveal: open doors slide out of the way, blocked
   *  doors flash red and continue to block. */
  private handleDoorCollisions(now: number) {
    if (!this.doorDash) return;
    if (this.player.state === "hit") return;
    const s = this.runState.distance;
    const l = this.player.laneOffset + this.player.slotOffset;
    const playerR = 0.42;
    const DOOR_TOP_Y = 2.4;
    if (this.player.y > DOOR_TOP_Y - 0.2) return; // jumped clear
    for (const d of this.doorDash.doors) {
      // Approach-only check: only collide when the player is moving INTO the
      // door from in front of it.
      if (s > d.s + d.halfDepth) continue;
      if (Math.abs(s - d.s) > d.halfDepth + playerR + 0.05) continue;
      if (Math.abs(l - d.l) > d.halfWidth + playerR) continue;
      // Reveal on contact (idempotent — the decor handle ignores re-reveals).
      this.doorDash.reveal(d, now);
      if (d.open) {
        // Open doors don't block — let the player pass through cleanly.
        continue;
      }
      // Blocked: push player back to just in front of the door, zero forward
      // speed, bounce away laterally.
      const stopS = d.s - d.halfDepth - playerR;
      this.runState.distance = Math.max(0, stopS);
      this.player.s = this.runState.distance;
      this.runState.speed = Math.min(this.runState.speed, 0);
      const sideSign = l < d.l ? -1 : 1;
      this.player.applyImpulse(sideSign * 1.6, 0);
      if (now >= this.runState.invulnerableUntil) {
        this.runState.invulnerableUntil = now + 350;
        sfx.hit();
        this.shake(now, 140, 0.12);
      }
      break;
    }
  }

  /** One-shot start-spawn snap. Casts a long downward ray from well above
   *  the player at the spawn point and finds the TOPMOST support mesh,
   *  unrestricted by the "above-me" / "below-me" culls used by the
   *  per-frame raycaster. Lifts the player onto that surface so they
   *  don't visually start inside the island deck during the countdown
   *  flyover. Safe for every course mode — falls back gracefully if no
   *  support is found. */
  private snapPlayerToTopmostSupportAtSpawn() {
    if (this.supportMeshes.length === 0) return;
    // Force world matrices fresh — meshes added to courseRoot this
    // frame haven't been auto-updated yet, and the raycast operates
    // in world space.
    this.scene.scene.updateMatrixWorld(true);
    const lift = this.courseRoot.position.y;
    const playerPos = this.player.group.position; // local to courseRoot
    const groundY = this.path.heightAt(this.runState.distance); // local
    const worldX = playerPos.x + this.courseRoot.position.x;
    const worldZ = playerPos.z + this.courseRoot.position.z;
    // Cast from well above the level so tall decks/arches still sit
    // beneath the ray's start point.
    _supportOrigin.set(worldX, groundY + 20 + lift, worldZ);
    this.supportRaycaster.set(_supportOrigin, _supportDown);
    this.supportRaycaster.far = 30;
    const hits = this.supportRaycaster.intersectObjects(this.supportMeshes, false);
    let bestLocalY = -Infinity;
    for (const hit of hits) {
      if (hit.object === this.player.group) continue;
      const hitLocalY = hit.point.y - lift;
      // Surface has to be at or above the path's ground line — anything
      // beneath is a hanging decoration like a rim or base.
      if (hitLocalY < groundY - 0.05) continue;
      if (hitLocalY > bestLocalY) bestLocalY = hitLocalY;
    }
    if (bestLocalY !== -Infinity) {
      this.player.landOn(bestLocalY - groundY);
      this.hasStandableSupport = true;
    }
  }

  private resolveStandableSurface() {
    this.hasStandableSupport = false;
    // Only skip the raycast when the player is genuinely jumping UP
    // (vy > 0.2). The OLD code also skipped during the "hit" state,
    // but that meant getting bumped by a ball / spinner left the
    // raycaster off the entire knockdown duration and gravity dropped
    // the player straight through the floor. Sandbox rule: collision
    // comes from real meshes, every frame.
    if (this.player.vy > 0.2) return;
    // Maze-rush sections: lane ribbons aren't in supportMeshes (they're at
    // varying Y per (lane, s) — the raycaster would catch the wrong lane
    // when lanes cross). When committed to a lane and inside its lateral
    // bounds the player is grounded. While in a between-lane fall they're
    // explicitly unsupported (gravity owns their Y).
    if (this.path.mazeRush) {
      if (this.mazeRushFallY != null) {
        // Falling between lanes — leave hasStandableSupport=false so the
        // player's jump/grounded state reflects that they're airborne.
        return;
      }
      if (this.mazeRushLaneIdx >= 0) {
        const s = this.runState.distance;
        const warp = this.path.mazeRushLaneWarpAt(this.mazeRushLaneIdx, s);
        const lateral = this.player.laneOffset + this.player.slotOffset;
        if (Math.abs(lateral - warp.dx) <= this.path.mazeRushLaneHalfWidth()) {
          this.hasStandableSupport = true;
          // Clamp physics-y back to the lane every frame so gravity
          // (applied in player.update before updateMazeRushLane runs)
          // can't accumulate and sink the player through the ribbon.
          // Skip while jumping — only catch on descent.
          if (this.player.vy <= 0.05) this.player.landOn(0);
          return;
        }
      }
    }
    if (this.supportMeshes.length === 0) return;
    // Raycast in world space — courseRoot is lifted, every mesh is in
    // world coords. Convert hits back to course-local before computing
    // the player's path-relative landing height.
    const lift = this.courseRoot.position.y;
    const groundY = this.path.heightAt(this.runState.distance);          // local
    const footYLocal = this.player.group.position.y + this.player.y;     // local
    const footYWorld = footYLocal + lift;                                // world
    const playerWorldX = this.player.group.position.x + this.courseRoot.position.x;
    const playerWorldZ = this.player.group.position.z + this.courseRoot.position.z;
    _supportOrigin.set(playerWorldX, footYWorld + 2.2, playerWorldZ);
    this.supportRaycaster.set(_supportOrigin, _supportDown);
    this.supportRaycaster.far = 4.8;
    const hits = this.supportRaycaster.intersectObjects(this.supportMeshes, false);
    // Anti-tunnel: when descending fast (e.g. after a spinner knockdown), a
    // solid floor can end up ABOVE the foot within a single frame because the
    // player passed straight through it. Expand the upper catch window by the
    // distance fallen this frame so the floor still catches them and snaps
    // them back on top, instead of letting them tunnel through. Only while
    // genuinely falling, so standing/jumping under an arch never snaps up.
    const fallingFast = this.player.vy < -1;
    const sweptUp = fallingFast ? Math.max(0, -this.player.vy) * this.lastDt : 0;
    for (const hit of hits) {
      if (hit.object === this.player.group) continue;
      const hitLocalY = hit.point.y - lift;
      // The OLD ground-line filter (`< groundY - 0.05`) rejected the
      // space-ship lower-rescue deck because it sits BELOW heightAt by
      // design. Sandbox rule: any visible mesh is a valid floor —
      // trust the foot-window check alone.
      //
      // Don't snap to a surface that's well below the player's feet —
      // otherwise mid-air descents get yanked onto the platform top the
      // moment the raycast can see it (visible as a jump-cut glitch).
      if (hitLocalY < footYLocal - 0.45) continue;
      // Standard "is this above me?" cull, widened by the swept fall so a
      // floor we tunnelled through this frame still counts.
      if (hitLocalY > footYLocal + 0.34 + sweptUp) continue;
      this.hasStandableSupport = true;
      this.player.landOn(hitLocalY - groundY);
      return;
    }
    // River-boat water-walk FALLBACK: if the player's footprint is over
    // the river (no dock / boat under them) and they're close to the
    // water level, snap to the surface so they can wade across. Runs
    // AFTER the raycast so boats / docks always take priority — fixes
    // the bug where the player could only walk on water and never
    // jumped onto a passing boat.
    if (this.path.riverBoat && this.isPlayerInRiverWater() && this.player.y < 0.4) {
      this.hasStandableSupport = true;
      this.player.landOn(-0.25 - this.path.heightAt(this.runState.distance));
      return;
    }
    this.player.clearSurfaceSupport();
  }

  private resolveChallenge(wordId: string, correct: boolean) {
    this.wordOutcomes.push({ wordId, correct });
    if (correct) {
      this.runState.coins += this.quizCorrectCoins + this.quizBonusCoins;
      this.runState.score += 50;
      // No speed surge, no boost animation — the audio + coin reward is enough.
      // Previously the forward burst made the camera lurch which read as a hop.
      sfx.correct();
    } else {
      this.runState.hearts = Math.max(0, this.runState.hearts - 1);
      sfx.wrong();
    }
    this.emitState();
    this.resume();
  }

  private finishRun(finished: boolean, opts: { dramatic?: boolean } = {}) {
    if (this.runState.finishedAt != null) return;
    this.runState.finishedAt = performance.now();
    if (finished) {
      this.runState.coins += this.winnerChestCoins;
      sfx.finish();
    }
    const outcome: RunOutcome = {
      score: this.runState.score,
      timeMs: Math.round(this.runState.finishedAt - this.runState.startedAt),
      coinsEarned: this.runState.coins,
      hearts: this.runState.hearts,
      stumbles: this.runState.stumbles,
      wordOutcomes: this.wordOutcomes,
      finished,
    };
    if (!finished) {
      // Survival uses a clean DNF (dramatic:false): no shake / crumble /
      // slow-mo death cinematic — just emit the outcome and end the run.
      if (opts.dramatic === false) {
        this.onEvent({ type: "death", outcome });
        return;
      }
      this.shake(performance.now(), 700, 0.9);
      this.player.crumble();
      this.dying = true;
      this.deathStartedAt = performance.now();
      this.pendingDeath = outcome;
      return;
    }
    this.onEvent({ type: "finish", outcome });
  }

  /** Survival fall (Level 10). Reports this player's fall with their survival
   *  time (timeMs) so results can rank by who lasted longest — the last to
   *  fall wins, and "die first loses" falls out of ranking by time descending.
   *  In multiplayer the "finish" event drops the player into spectator to
   *  watch whoever's still up; solo, they were the last one standing → win. */
  private reportSurvivalFall() {
    if (this.runState.finishedAt != null) return;
    this.runState.finishedAt = performance.now();
    this.dying = false;
    const solo = this.ghosts.size === 0;
    if (solo) {
      this.runState.coins += this.winnerChestCoins;
      sfx.finish();
    }
    const outcome: RunOutcome = {
      score: this.runState.score,
      timeMs: Math.round(this.runState.finishedAt - this.runState.startedAt),
      coinsEarned: this.runState.coins,
      hearts: this.runState.hearts,
      stumbles: this.runState.stumbles,
      wordOutcomes: this.wordOutcomes,
      finished: solo,
    };
    this.onEvent({ type: "finish", outcome });
  }

  private shake(now: number, durationMs: number, amplitude: number) {
    if (now + durationMs > this.shakeUntil) {
      this.shakeUntil = now + durationMs;
      this.shakeStart = now;
    }
    this.shakeAmp = Math.max(this.shakeAmp, amplitude);
  }

  private applyCamera(z: number, now: number) {
    // Pre-race flyover only runs while the game is paused (i.e. before
    // GO!) and still inside the configured duration window. The previous
    // gate also required `z < 0.05` which broke when the start-line offset
    // moved the player to z=5 — now we just gate on paused + duration.
    if (
      this.flyoverStartedAt &&
      this.runState.paused &&
      now - this.flyoverStartedAt < this.flyoverDurationMs
    ) {
      this.applyStartFlyover(now);
      return;
    }

    // courseRoot is lifted by floatingLevelBaseY; player.group.position
    // is local to it. Camera sits on scene root (un-lifted) so we add
    // the courseRoot offset to put the camera in the right world space.
    // `player.y` is the body's vertical offset on top of the path
    // anchor (jumping / falling) — INCLUDE IT here so the camera
    // follows the body when the player drops off a platform, instead
    // of staying glued to the path-anchor altitude.
    const lift = this.courseRoot.position.y;
    const playerLocal = this.player.group.position;
    // Smooth the vertical follow — `playerLocal.y + player.y` JUMPS by
    // ~1.4m when the player lands on a higher step (level 3 pyramid).
    // Lerping it before feeding the camera removes the visible snap
    // while still tracking real movement (gravity falls, big jumps).
    const targetWorldY = playerLocal.y + this.player.y + lift;
    if (!Number.isFinite(this.camSmoothedY)) this.camSmoothedY = targetWorldY;
    // 8 rad/s lerp — tight enough to hug normal jumps + falls but slow
    // enough to glide over the 1.4m step pop.
    const ySmooth = 1 - Math.exp(-8 * this.lastDt);
    this.camSmoothedY += (targetWorldY - this.camSmoothedY) * ySmooth;
    const playerPos = _camPlayerWorld.set(
      playerLocal.x + this.courseRoot.position.x,
      this.camSmoothedY,
      playerLocal.z + this.courseRoot.position.z,
    );
    // Camera follows the COURSE direction (path tangent), not the player's
    // facing. This keeps the camera oriented along the track even when the
    // player turns around to walk backward, which prevents the camera from
    // spinning to stay behind a reversed player.
    const tan = this.path.tangentAt(this.runState.distance, _camForward);
    const forward = _camForward.set(tan.x, 0, tan.z);
    if (forward.lengthSq() < 0.001) forward.set(0, 0, 1);
    forward.normalize();
    const dt = this.lastDt;
    const lookActive = Math.hypot(this.lookAxisX, this.lookAxisY) > 0.04;
    if (lookActive) {
      // Yaw/pitch input is now expressed in rad/s and scaled by dt so the
      // sensitivity is identical at 30, 60, and 120 FPS.
      this.cameraYawOffset = THREE.MathUtils.clamp(
        this.cameraYawOffset - this.lookAxisX * 3.1 * dt,
        -Math.PI * 0.75,
        Math.PI * 0.75,
      );
      this.cameraPitchOffset = THREE.MathUtils.clamp(
        this.cameraPitchOffset + this.lookAxisY * 1.9 * dt,
        -0.75,
        0.6,
      );
    } else {
      // Slower auto-return — feels "sticky" instead of snapping back.
      const decay = Math.exp(-1.2 * dt);
      this.cameraYawOffset *= decay;
      this.cameraPitchOffset *= decay;
    }
    _camOrbitForward.copy(forward).applyAxisAngle(_worldUp, this.cameraYawOffset).normalize();
    // Speed-scaled look-ahead: stand-still cam is close, full-speed cam looks further.
    const maxSpeed = (this.config.runSpeed ?? RUNNER.baseSpeed) + 6;
    const speedFrac = THREE.MathUtils.clamp(this.runState.speed / maxSpeed, 0, 1);
    // Closer + slightly lower camera (Fall Guys feel). Constants in theme.ts.
    let lookAhead: number = CAMERA_TUNING.lookAhead + speedFrac * 2.6;
    let camBack: number = CAMERA_TUNING.baseBack + speedFrac * CAMERA_TUNING.speedBackBoost;
    let camHeight: number = CAMERA_TUNING.baseHeight;
    let lookHeight: number = CAMERA_TUNING.lookHeightOffset;
    // Survival mode: pulled back + higher than the chase cam, but tuned
    // tighter than the first pass so the player and the spinners read big.
    if (this.doubleSpinner) {
      // ~30% tighter than the previous survival framing so the player +
      // spinners read bigger on screen.
      camBack = 11.2;
      camHeight = 7;
      lookAhead = 2.1;
      lookHeight = 0.8;
    }
    const desired = _camDesired
      .copy(playerPos)
      .addScaledVector(_camOrbitForward, -camBack);
    desired.y = playerPos.y + camHeight + Math.sin(this.cameraPitchOffset) * 3.0;
    const look = _camLook.copy(playerPos).addScaledVector(_camOrbitForward, lookAhead);
    look.y = playerPos.y + lookHeight;
    if (now < this.shakeUntil) {
      const total = Math.max(1, this.shakeUntil - this.shakeStart);
      const remaining = (this.shakeUntil - now) / total;
      const a = this.shakeAmp * remaining;
      desired.x += (Math.random() - 0.5) * a;
      desired.y += (Math.random() - 0.5) * a;
      desired.z += (Math.random() - 0.5) * a * 0.4;
    } else {
      this.shakeAmp = 0;
    }
    // Time-normalized lerp — slightly snappier follow.
    const camLerp = 1 - Math.exp(-CAMERA_TUNING.lerpRate * dt);
    this.scene.camera.position.lerp(desired, camLerp);
    this.scene.camera.lookAt(look.x, look.y, look.z);
    this.scene.followShadows(playerPos.x, playerPos.z);
  }

  private applyDeathCamera(now: number) {
    const t = Math.min(1, (now - this.deathStartedAt) / 1450);
    const p = easeInOutCubic(t);
    const lift = this.courseRoot.position.y;
    const targetLocal = this.player.group.position;
    const target = _camPlayerWorld.set(
      targetLocal.x + this.courseRoot.position.x,
      targetLocal.y + this.player.y + lift,
      targetLocal.z + this.courseRoot.position.z,
    );
    const tan = this.path.tangentAt(this.runState.distance, _deathTan);
    const side = _deathSide.set(tan.z, 0, -tan.x).normalize();
    const camDistance = THREE.MathUtils.lerp(10.5, 3.4, p);
    const sideDrift = THREE.MathUtils.lerp(0, 1.25, p);
    const height = THREE.MathUtils.lerp(5.4, 2.25, p);
    const cam = _deathCam
      .copy(target)
      .addScaledVector(tan, -camDistance)
      .addScaledVector(side, sideDrift);
    cam.y = lift + height;
    this.scene.camera.position.copy(cam);
    this.scene.camera.lookAt(target.x, lift + 0.95 + Math.sin(t * Math.PI) * 0.25, target.z);
    this.scene.followShadows(target.x, target.z);
  }

  private pickSpectateGhost(): Ghost | null {
    let best: Ghost | null = null;
    for (const g of this.ghosts.values()) {
      const state = this.ghostStates.get(g.id);
      if (state === "finished") continue;
      if (!best || g.targetS > best.targetS) best = g;
    }
    if (best) return best;
    for (const g of this.ghosts.values()) {
      if (!best || g.targetS > best.targetS) best = g;
    }
    return best;
  }

  /** Spectator orbit — drag-rotated by the local player (see the
   *  pointer handler attached in the Game constructor). Defaults to 0
   *  (behind-the-shoulder) and is clamped to a comfortable pitch range. */
  private spectateOrbitYaw = 0;
  private spectateOrbitPitch = 0;

  /** Public API for the input layer to nudge the spectator camera. */
  rotateSpectatorCamera(dxRad: number, dyRad: number) {
    this.spectateOrbitYaw += dxRad;
    this.spectateOrbitPitch = THREE.MathUtils.clamp(
      this.spectateOrbitPitch + dyRad,
      -Math.PI / 3,
      Math.PI / 3,
    );
  }

  private applySpectatorCamera(targetGhost: Ghost, now: number) {
    // Same behind-the-shoulder framing as applyCamera, but the player
    // can drag-rotate around the spectated runner via `rotateSpectatorCamera`.
    const s = THREE.MathUtils.clamp(targetGhost.s, 0, this.path.length);
    const tan = this.path.tangentAt(s, _spectateTan);
    const forward = _camForward.set(tan.x, 0, tan.z);
    if (forward.lengthSq() < 0.001) forward.set(0, 0, 1);
    forward.normalize();
    // Apply the drag-rotation yaw around world-up to the forward vector.
    if (this.spectateOrbitYaw !== 0) {
      forward.applyAxisAngle(_worldUp, this.spectateOrbitYaw);
    }
    const lift = this.courseRoot.position.y;
    const ghostLocal = targetGhost.group.position;
    const ghostPos = _camPlayerWorld.set(
      ghostLocal.x + this.courseRoot.position.x,
      ghostLocal.y + lift,
      ghostLocal.z + this.courseRoot.position.z,
    );
    const maxSpeed = (this.config.runSpeed ?? RUNNER.baseSpeed) + 6;
    const ghostSpeed = Math.abs(targetGhost.getVelocityS?.() ?? 0);
    const speedFrac = THREE.MathUtils.clamp(ghostSpeed / maxSpeed, 0, 1);
    const lookAhead = 2.0 + speedFrac * 3.4;
    const camBack = 5.4 + speedFrac * 1.2;
    const desired = _spectateCam
      .copy(ghostPos)
      .addScaledVector(forward, -camBack);
    // Pitch adds vertical offset proportional to camBack so it feels like
    // tilting the camera, not floating it away.
    const pitchY = Math.sin(this.spectateOrbitPitch) * camBack;
    desired.y = ghostPos.y + 2.65 + pitchY;
    const look = _spectateLook.copy(ghostPos).addScaledVector(forward, lookAhead);
    look.y = ghostPos.y + 0.75;
    const intro = Math.min(1, Math.max(0.04, (now - this.deathStartedAt - 2600) / 900));
    const dt = this.lastDt;
    const blend = intro < 1 ? intro : 1 - Math.exp(-20 * dt);
    this.scene.camera.position.lerp(desired, blend);
    this.scene.camera.lookAt(look.x, look.y, look.z);
    this.scene.followShadows(ghostPos.x, ghostPos.z);
  }

  private applyStartFlyover(now: number) {
    const t = Math.min(1, (now - this.flyoverStartedAt) / this.flyoverDurationMs);
    const lift = this.courseRoot.position.y;
    if (t < 0.76) {
      // Phase 1: glide from the finish line back to the start, riding above
      // the path. Camera & look-at Y are anchored to the path's local height
      // so the camera stays ABOVE the terrain on courses that ramp up (Hit
      // Parade) — previously the fixed Y=9.7 was below the 22m-tall finish.
      const p = easeInOutCubic(t / 0.76);
      const s = THREE.MathUtils.lerp(this.path.length - 22, 18, p);
      const lookS = THREE.MathUtils.lerp(this.path.length - 48, 0, p);
      const cam = this.path.positionAt(Math.max(0, s), 0, _flyCam);
      const look = this.path.positionAt(Math.min(this.path.length, lookS), 0, _flyLook);
      const sideDrift = Math.sin(p * Math.PI) * 5.8;
      // Above-path camera height: high over the finish, descending toward the
      // start. Add to cam.y so a sloped course doesn't drop the camera into
      // the geometry.
      const camHeight = THREE.MathUtils.lerp(14, 8.2, p);
      this.scene.camera.position.set(cam.x + sideDrift, cam.y + camHeight + lift, cam.z + 8 - p * 2);
      // Look-at also rides the path height + a head-height offset.
      this.scene.camera.lookAt(look.x, look.y + 1.6 + Math.sin(p * Math.PI) * 1.4 + lift, look.z);
      this.scene.followShadows(this.player.group.position.x, this.player.group.position.z);
      return;
    }

    const orbitT = easeInOutCubic((t - 0.76) / 0.24);
    const start = this.path.positionAt(0, 0, _flyStart);
    const tan = this.path.tangentAt(0, _flyTan);
    const side = _flySide.set(tan.z, 0, -tan.x).normalize();
    const angle = orbitT * Math.PI;
    const radius = THREE.MathUtils.lerp(9.0, 12.0, orbitT);
    const cam = _flyCam
      .copy(start)
      .addScaledVector(tan, Math.cos(angle) * radius)
      .addScaledVector(side, Math.sin(angle) * 5.3);
    cam.y = start.y + THREE.MathUtils.lerp(7.0, 5.25, orbitT) + lift;
    this.scene.camera.position.copy(cam);
    const look = _flyLook.copy(start).addScaledVector(tan, THREE.MathUtils.lerp(0.5, 14, orbitT));
    this.scene.camera.lookAt(look.x, start.y + 1.45 + lift, look.z);
    this.scene.followShadows(this.player.group.position.x, this.player.group.position.z);
  }

  private updateCountdown(now: number) {
    if (!this.countdownGroup || !this.countdownLabel) return;
    const isGo = this.countdownLabel === "GO!";
    const cam = this.scene.camera;
    // Place the text along the camera's forward axis at a distance that
    // sweeps from far away → past the camera, so the number rushes straight
    // through the viewer each beat.
    const fwd = cam.getWorldDirection(_countdownFwd);
    const t = (now - this.countdownChangedAt) / 1000;

    let dist: number;
    let opacity: number;
    let scale: number;
    if (isGo) {
      // GO! flies in fast and parks at a readable distance with a punchy pop.
      const inP = Math.min(1, t / 0.32);
      dist = THREE.MathUtils.lerp(40, 11, 1 - Math.pow(1 - inP, 3));
      const pop = 1 + Math.sin(Math.min(1, t / 0.85) * Math.PI) * 0.45;
      scale = 1.25 * pop;
      opacity = 1;
    } else {
      // 3 / 2 / 1 accelerate out of the distance and blow past the camera.
      const flyDur = 0.82;
      const p = t / flyDur;                          // 0 → 1 (and beyond)
      const ease = p * p;                            // ease-in: rushes as it nears
      dist = THREE.MathUtils.lerp(46, -6, ease);     // ends behind the camera
      scale = 1;
      opacity = p < 1 ? 1 : Math.max(0, 1 - (p - 1) / 0.25);
    }

    this.countdownGroup.position.copy(cam.position).addScaledVector(fwd, dist);
    this.countdownGroup.quaternion.copy(cam.quaternion);
    this.countdownGroup.scale.setScalar(scale);
    if (this.countdownTextMat) this.countdownTextMat.opacity = opacity;

    for (let i = 0; i < this.countdownRings.length; i++) {
      const ring = this.countdownRings[i]!;
      ring.rotation.z += (i === 0 ? 0.06 : -0.085);
      const mat = (ring as THREE.Mesh).material as THREE.MeshBasicMaterial;
      mat.opacity = (isGo ? 0.5 : 0.6 + Math.sin(now * 0.006 + i) * 0.12) * opacity;
      ring.scale.setScalar(1 + i * 0.08 + Math.sin(now * 0.005 + i) * 0.04);
    }
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange as EventListener);
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = null;
    for (const id of this.fullscreenResizeTimers) clearTimeout(id);
    this.fullscreenResizeTimers = [];
    this.cleanupCanvasListeners?.();
    this.cleanupCanvasListeners = null;
    this.input.dispose();
    this.countdownTextMesh?.geometry.dispose();
    this.countdownTextMat?.dispose();
    this.scene.dispose();
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function obstacleCenterOffset(o: ObstacleController): number {
  if (o.getLateralOffset) return o.getLateralOffset();
  const lanes = o.lanes();
  if (lanes.length === 0) return 0;
  const total = lanes.reduce((sum, lane) => sum + (LANE_POSITIONS[lane] ?? 0), 0);
  return total / lanes.length;
}

function obstacleBlockDistance(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 1.35;
    case "log":
    case "spike-roller":
      return 1.25;
    case "lava":
      return 1.15;
    case "barrier":
    case "crate":
    default:
      return 0.95;
  }
}

function obstacleContactDepth(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 1.55;
    case "sweeper":
      return 1.65;
    case "lava":
    case "spike-roller":
      return 1.25;
    default:
      return 1.1;
  }
}

function obstacleLaneRadius(o: ObstacleController): number {
  switch (o.kind) {
    case "boulder":
      return 0.92;
    case "sweeper":
      return 1.12;
    case "lava":
    case "spike-roller":
      return 0.82;
    default:
      return 0.72;
  }
}

function obstacleRecoveryMs(o: ObstacleController): number {
  if (o.kind === "sweeper") return 900;
  return o.kind === "boulder" ? 620 : 520;
}

function obstacleImpactImpulse(o: ObstacleController) {
  switch (o.kind) {
    case "boulder":
      return { lateral: 5.6, vertical: 2.2, forward: 13.8, slowMs: 1050, shakeMs: 520, shake: 0.78 };
    case "sweeper":
      return { lateral: 7.4, vertical: 4.4, forward: 10.8, slowMs: 950, shakeMs: 460, shake: 0.66 };
    case "spike-roller":
    case "log":
      return { lateral: 3.1, vertical: 3.2, forward: 7.2, slowMs: 560, shakeMs: 260, shake: 0.38 };
    default:
      return { lateral: 2.25, vertical: 2.7, forward: 4.8, slowMs: 450, shakeMs: 210, shake: 0.28 };
  }
}

const COURSE_FALL_HALF_WIDTH = 7.05;
const _camDesired = new THREE.Vector3();
const _camForward = new THREE.Vector3();
const _camPlayerWorld = new THREE.Vector3();
const _camOrbitForward = new THREE.Vector3();
const _camLook = new THREE.Vector3();
const _countdownFwd = new THREE.Vector3();
const _deathCam = new THREE.Vector3();
const _deathSide = new THREE.Vector3();
const _deathTan = new THREE.Vector3();
const _spectateCam = new THREE.Vector3();
const _spectateLook = new THREE.Vector3();
const _spectateSide = new THREE.Vector3();
const _spectateTan = new THREE.Vector3();
const _supportDown = new THREE.Vector3(0, -1, 0);
const _supportOrigin = new THREE.Vector3();
const _fallSide = new THREE.Vector3();
const _fallTan = new THREE.Vector3();
const _moveSide = new THREE.Vector3();
const _moveTan = new THREE.Vector3();
const _moveCameraForward = new THREE.Vector3();
const _moveCameraRight = new THREE.Vector3();
const _moveWorldDir = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);
const _flyCam = new THREE.Vector3();
const _flyLook = new THREE.Vector3();
const _flyStart = new THREE.Vector3();
const _flyTan = new THREE.Vector3();
const _flySide = new THREE.Vector3();

